// La superficie de frontend del módulo.
//
// El armazón descubre este fichero solo (modules/*/frontend/index.js) y mezcla
// lo que exporta: rutas, menú, textos — y `overlays`, lo que tiene que estar
// montado en TODAS las pantallas: la llamada que suena mientras alguien está en
// una factura.
//
// Las pantallas son pocas a propósito (§8.1): el canal (la lista y la
// conversación), la llamada, y el repaso. Lo demás son atajos que resuelven y
// se apartan: «hablar aquí» desde la ficha de un registro y el aviso de una
// llamada.

import { defineAsyncComponent } from 'vue'

import { withAnalytics } from './i18n/analytics.js'
import { unreadRadio } from './unread.js'
import assistantMessages from './assistantMessages.js'

export const routes = [
  {
    path: '/comms',
    name: 'comms',
    component: () => import('./pages/ChannelPage.vue'),
    meta: { permission: ['comms:use', 'comms:oversee'], title: 'comms.title' },
  },
  {
    path: '/comms/t/:id',
    name: 'comms-thread',
    component: () => import('./pages/ChannelPage.vue'),
    meta: { permission: ['comms:use', 'comms:oversee'], title: 'comms.title' },
  },
  {
    path: '/comms/about/:type/:id',
    name: 'comms-subject',
    component: () => import('./pages/SubjectPage.vue'),
    meta: { permission: 'comms:use', title: 'comms.title' },
  },
  {
    path: '/comms/call/:id',
    name: 'comms-call',
    component: () => import('./pages/CallPage.vue'),
    meta: { permission: 'comms:use', title: 'comms.title' },
  },
  {
    path: '/comms/meetings',
    name: 'comms-meetings',
    component: () => import('./pages/MeetingsPage.vue'),
    meta: { permission: 'comms:use', title: 'comms.meetings.title' },
  },
  {
    path: '/comms/meet/:id',
    name: 'comms-meeting',
    component: () => import('./pages/MeetingRoomPage.vue'),
    meta: { permission: 'comms:use', title: 'comms.meetings.title' },
  },

  // -- La puerta de Radio Meetings, para quien no tiene cuenta -----------------
  //
  // `public: true` la deja pasar sin sesión y `layout: 'auth'` la pinta sin el
  // menú ni la barra del ERP: el invitado ve la empresa y la reunión, y nada
  // más. Lo que puede hacer lo decide su token en el servidor, no esta ruta.
  {
    path: '/meet/:token',
    name: 'comms-meet-guest',
    component: () => import('./pages/GuestMeetingPage.vue'),
    meta: { public: true, layout: 'auth', title: 'comms.meet.pageTitle' },
  },
  {
    path: '/comms/review',
    name: 'comms-review',
    component: () => import('./pages/ReviewPage.vue'),
    meta: { permission: 'comms:use', title: 'comms.review.title' },
  },
]

export const navigation = [
  {
    to: '/comms',
    labelKey: 'comms.title',
    permission: 'comms:use',
    icon: 'radio',
    group: 'business',
    // Los mensajes sin leer, también desde cualquier otra pantalla (unread.js).
    badge: unreadRadio,
    // Y junto al saludo del inicio, con ese mismo contador: es lo que se mira
    // al abrir el ERP en el móvil, después de fichar.
    greeting: true,
  },
  {
    to: '/comms/meetings',
    labelKey: 'comms.meetings.title',
    permission: 'comms:use',
    icon: 'video',
    group: 'business',
  },
]

// En todas las pantallas, con sesión y con el módulo corriendo de verdad.
export const overlays = [
  {
    id: 'call',
    permission: 'comms:use',
    component: defineAsyncComponent(() => import('./components/CallOverlay.vue')),
  },
]

// Los textos de este módulo, en los cuatro idiomas y en el orden de siempre
// (es · ca · en · fr), incluido cómo se llama en las pantallas de la
// plataforma: Roles, Ajustes, Auditoría y el asistente (§4.2).
const baseMessages = {
  es: {
    comms: {
      meet: {
        hostLeft: 'Quien organiza ha salido. La reunión sigue: puedes seguir hablando con los demás.',
        another: {
          title: 'Seguir desde otro dispositivo',
          hint: 'Escanea el código con el móvil o la tablet: entras como tú, sin volver a pedir paso, con el micrófono y el altavoz apagados.',
          make: 'Mostrar el código',
          copy: 'Copiar el enlace',
          expires: 'Vale una sola vez y caduca en 10 minutos.',
        },
        replaced: { title: 'La reunión sigue en otra pestaña.', hint: 'Has abierto el enlace en otra pestaña o ventana, y la reunión ha pasado allí.', again: 'Seguir aquí' },
        pageTitle: 'Reunión',
        brandFallback: 'Reunión',
        kicker: 'Reunión por vídeo',
        organizedBy: 'Organiza {name}',
        calendar: { add: 'Añadir a mi calendario:', google: 'Google', outlook: 'Outlook', ics: 'Archivo para iPhone o Android (.ics)', details: 'Reunión por vídeo con {who}. Entra desde este enlace, sin instalar nada: {url}' },
        gone: {
          title: 'Esta reunión ha finalizado.',
          hint: 'El enlace ya no está activo. Si crees que es un error, pide uno nuevo a quien te invitó.',
        },
        removed: { title: 'Ya no estás en esta reunión.', hint: 'Quien la organiza te ha sacado de la sala.' },
        ended: { title: 'La reunión ha terminado.', hint: 'Quien la organizaba la ha cerrado para todos. Puedes cerrar esta página.' },
      left: { title: 'Has salido de la reunión.', hint: 'Si ha sido sin querer, vuelve a entrar. Si no, puedes cerrar esta página.', again: 'Volver a entrar' },
        cameraOff: 'Cámara apagada',
        noPreview: 'Prueba tu cámara y tu micrófono antes de entrar',
        micTest: 'Micrófono',
        tryDevices: 'Probar cámara y micrófono',
        name: 'Tu nombre',
        namePlaceholder: 'Nombre y apellido',
        email: 'Correo',
        optional: '(opcional)',
        recordingTitle: 'Esta reunión se graba.',
        recordingNotice: 'Esta reunión puede grabarse. Quien la organiza decide cuándo se graba, y lo verás en pantalla mientras ocurra.',
        recordingConsent: 'Acepto que se grabe mi voz durante la reunión.',
        aiConsent: 'Acepto que lo que diga se transcriba y lo resuma un asistente.',
        requestAccess: 'Solicitar acceso',
        entry: { title: '¿Cómo quieres entrar?', micOn: 'Con micrófono', micOff: 'Silenciado', cameraOn: 'Con cámara', cameraOff: 'Sin cámara' },
        requestHint: 'Quien organiza la reunión verá tu nombre y te dejará pasar.',
        waiting: {
          title: 'Esperando a que te dejen entrar…',
          hint: 'Ya saben que estás aquí. No cierres esta página.',
          cancel: 'Cancelar la solicitud',
        },
        rejected: { title: 'No te han dejado entrar', hint: 'Puedes volver a pedirlo dentro de un momento.', again: 'Volver a pedir acceso' },
        admitted: { title: 'Ya estás dentro', hint: 'La sala se abre en cuanto entre quien organiza.' },
        room: { alone: 'Esperando a los demás…' },
        chat: { attach: 'Adjuntar una foto, un vídeo o un documento', location: 'Enviar mi ubicación', title: 'Chat', empty: 'Aquí se ven los mensajes y archivos de esta reunión.' },
        errors: {
          device_link_gone: 'Este enlace ya se ha usado o ha caducado. Pide uno nuevo desde el otro dispositivo.',
          device_link_failed: 'No se ha podido preparar el enlace. Inténtalo de nuevo.',
          request_failed: 'No se ha podido enviar. Inténtalo de nuevo.',
          recording_consent: 'Esta reunión se graba: para entrar tienes que aceptarlo.',
          removed: 'Te sacaron de esta reunión: no puedes volver a entrar.',
          too_many: 'Has pedido entrar demasiadas veces.',
          wait: 'Espera unos segundos antes de volver a pedirlo.',
          door_busy: 'Hay mucha gente esperando para entrar. Vuelve a intentarlo en un momento.',
          gone: 'Esta reunión ha finalizado.',
          meeting_over: 'Esta reunión ha finalizado.',
          meeting_not_started: 'La sala aún no está abierta.',
          call_failed: 'No se ha podido entrar en la sala. Inténtalo de nuevo.',
        },
      },
      meetings: {
        admitAll: 'Admitir a todos',
        waitingIn: '{count} esperando en «{title}»',
        title: 'Reuniones',
        subtitle: 'Videollamadas con la oficina, el campo y gente de fuera',
        provider: 'Radio (videollamada)',
        kicker: 'Reunión',
        new: 'Nueva reunión',
        edit: 'Cambiar la reunión',
        create: 'Crear y copiar enlace',
        failed: 'No se han podido cargar tus reuniones',
        emptyTitle: 'No tienes reuniones',
        emptyHint: 'Crea una y comparte el enlace: quien no tiene cuenta entra desde el navegador, sin instalar nada.',
        notFound: 'Esta reunión no existe o no puedes verla',
        groups: { live: 'Ahora', next: 'Próximas', past: 'Anteriores' },
        status: { scheduled: 'Programada', live: 'En curso', ended: 'Terminada', cancelled: 'Cancelada' },
        guestsCount: '{count} invitado de fuera | {count} invitados de fuera',
        waitingCount: '{count} esperando',
        recorded: 'Se graba',
        aiOn: 'Asistente',
        organizer: 'Organiza {name}',
        lasted: 'Duró {minutes} min',
        join: 'Entrar con vídeo',
        joinLive: 'Entrar en la sala',
        joinAudio: 'Entrar solo con audio',
        enter: 'Entrar a la reunión',
        backToRoom: 'Volver a la sala',
        inOtherCall: 'Estás en otra llamada. Cuélgala para entrar.',
        wasCancelled: 'Esta reunión se canceló.',
        wasEnded: 'Esta reunión ha terminado.',
        openChat: 'Abrir la conversación de la reunión',
        openMeeting: 'Ver reunión',
        openInCalendar: 'Ver en el calendario',
        endForAll: 'Terminar para todos',
        endConfirm: 'La llamada acaba para todos, los invitados salen y el enlace deja de servir.',
        cancel: 'Cancelar la reunión',
        cancelConfirm: 'Nadie podrá entrar y el enlace dejará de servir.',
        link: 'Enlace para invitados',
        copyLink: 'Copiar enlace',
        copied: 'Copiado',
        copyFailed: 'No se ha podido copiar. Selecciona el enlace y cópialo a mano.',
        linkHint: 'Quien lo abra pedirá entrar y tú decides si pasa.',
        linkHintOpen: 'Sin sala de espera: quien tenga el enlace entra directamente.',
        expires: 'Sirve hasta el {when}.',
        revoked: 'El enlace está desactivado. Nadie de fuera puede entrar.',
        noLink: 'Solo quien organiza la reunión puede compartir el enlace.',
        noExternal: 'Esta reunión no admite gente de fuera.',
        regenerate: 'Enlace nuevo',
        regenerateConfirm: 'El enlace actual dejará de funcionar. Quien lo tenga necesitará el nuevo.',
        regenerated: 'Enlace nuevo listo. El anterior ya no sirve.',
        enableLink: 'Crear enlace',
        revoke: 'Desactivar enlace',
        revokeConfirm: 'Nadie más podrá pedir entrar. Quien ya está dentro sigue dentro.',
        door: 'Invitados de fuera',
        noGuests: 'Todavía no ha llamado nadie a la puerta.',
        decidedBy: 'por {name}',
        consentRecording: 'grabación',
        consentAi: 'transcripción',
        recording: {
          title: 'Grabación',
          loading: 'Buscando la grabación…',
          failed: 'No se ha podido cargar la grabación',
          none: 'No hay nada grabado de esta reunión. Se graba cuando está activado en Ajustes de Radio y en la reunión, y alguien más ha entrado.',
          whole: 'La reunión',
          part: 'Parte {n}',
          purged: 'El audio ya se borró por el plazo de conservación; queda lo que se dijo.',
          prepare: 'Preparar el audio',
          mixing: 'Juntando las voces… {done} de {total}',
          mixFailed: 'No se ha podido preparar el audio',
          speed: 'Velocidad',
          download: 'Descargar el audio',
          transcript: 'Lo que se dijo',
          transcriptPending: 'Transcribiendo… aparecerá aquí en unos minutos.',
          transcriptOff: 'Sin transcripción: la IA no está activada.',
        },
        admit: 'Admitir',
        reject: 'Rechazar',
        admitAnyway: 'Admitir',
        remove: 'Sacar',
        removeSure: '¿Sacar?',
        external: 'Externo',
        waitingBadge: 'Esperando',
        guestStates: {
          requested: 'Esperando',
          admitted: 'Admitido',
          connected: 'Conectado',
          rejected: 'Rechazado',
          left: 'Salió',
          removed: 'Expulsado',
          withdrawn: 'Se fue antes de entrar',
        },
        afterTitle: 'Lo que salió de la reunión',
        summaryPending: 'El asistente está preparando el resumen. Aparecerá aquí y en la conversación.',
        knocks: 'Personas esperando para entrar',
        knock: '{name} quiere entrar',
        again: 'lo pide por {count}.ª vez',
        moreKnocks: '{count} más esperando',
        fields: {
          title: 'Título',
          titlePlaceholder: 'Revisión de la obra con el proveedor',
          day: 'Día',
          start: 'Empieza',
          end: 'Acaba',
          internal: 'De la empresa',
          internalHint: 'Entran directamente, sin esperar.',
          searchPeople: 'Buscar a alguien…',
          guests: 'De fuera',
          guestsHint: 'Opcional: para reconocerles cuando llamen a la puerta. El enlace se comparte igual.',
          guestName: 'Nombre',
          guestEmail: 'Correo',
          addGuest: 'Añadir invitado',
          externalAccess: 'Pueden entrar personas de fuera con el enlace',
          waitingRoom: 'Sala de espera: yo decido quién pasa',
          ai: 'Asistente: resumen y propuestas al acabar',
          recording: 'Grabar la reunión',
          recordingHint: 'Se avisa a todos durante toda la reunión y los de fuera tienen que aceptarlo para entrar. Solo graba si la grabación está activada en los ajustes de Radio.',
          noWaitingRoomHint: 'Sin sala de espera, cualquiera con el enlace entra sin preguntar.',
          inCalendar: 'Se guarda en el calendario: cae en la agenda de cada persona de la empresa invitada, con su invitación.',
        },
        errors: {
          title: 'Ponle un título.',
          when: 'Revisa el día y las horas.',
          tooLong: 'Una reunión no puede durar más de 12 horas.',
          save: 'No se ha podido guardar. Inténtalo de nuevo.',
          decide: 'No se ha podido. Inténtalo de nuevo.',
        },
      },
      title: 'Radio',
      back: 'Volver al canal',
      retry: 'Reintentar',
      help: {
        title: 'Radio',
        subtitle: 'Hablar con la oficina y con el campo, llamar, mandar notas de voz, y dejar que el asistente apunte lo que se dice',
      },
      connection: {
        idle: 'Sin conectar',
        connecting: 'Conectando…',
        open: 'Conectado',
        retrying: 'Reconectando…',
        offline: 'Sin conexión',
      },
      list: {
        emptyTitle: 'Todavía no hablas con nadie',
        emptyHint: 'Toca «Nueva» y elige a una persona. Desde la ficha de una obra o de un sitio también se abre su conversación.',
        failed: 'No se han podido cargar tus conversaciones',
        failedHint: 'Comprueba la conexión y vuelve a intentarlo.',
        noMessages: 'Sin mensajes todavía',
        you: 'Tú',
        inCall: 'Llamada en curso',
        showArchived: 'Ver archivadas',
        hideArchived: 'Ocultar archivadas',
        noArchived: 'No hay nada archivado.',
        restore: 'Recuperar',
      },
      pane: {
        title: 'Elige una conversación',
        hint: 'O empieza una nueva. Mantén pulsado el micrófono para mandar una nota de voz.',
      },
      kinds: {
        text: 'Mensaje',
        voice: 'Nota de voz',
        photo: 'Foto',
        video: 'Vídeo',
        file: 'Archivo',
        location: 'Ubicación',
        system: 'Aviso',
        call: 'Llamada',
        proposal: 'Propuesta del asistente',
        deleted: 'Mensaje borrado',
      },
      subjects: {
        property: 'Emplazamiento',
        work: 'Obra',
        project: 'Proyecto',
        incident: 'Incidencia',
        work_order: 'Orden de trabajo',
        customer: 'Cliente',
        asset: 'Máquina',
        vehicle: 'Vehículo',
        production_order: 'Orden de fabricación',
        location: 'Almacén',
        team: 'Equipo',
      },
      presence: {
        online: 'En línea',
        offline: 'Desconectado',
        seen: 'Visto {when}',
        onlineCount: '{count} en línea',
      },
      new: {
        title: 'Nueva conversación',
        short: 'Nueva',
        person: 'Una persona',
        group: 'Un grupo',
        search: 'Busca por nombre',
        typeName: 'Escribe un nombre',
        nobody: 'No hay nadie con ese nombre',
        groupName: 'Nombre del grupo',
        groupPlaceholder: 'La cuadrilla, la oficina, los encargados…',
        create: 'Crear el grupo',
      },
      search: {
        placeholder: 'Buscar en tus conversaciones',
        inThread: 'Buscar',
        nothing: 'No se ha encontrado nada',
        mode: {
          semantic: 'Buscando por el significado',
          text: 'Buscando por las palabras',
        },
      },
      thread: {
        openRecord: 'Ver {kind}',
        tooOld: 'Ese mensaje es demasiado antiguo para cargarlo aquí.',
        loading: 'Cargando…',
        loadFailed: 'No se ha podido cargar la conversación',
        emptyTitle: 'Todavía no hay nada aquí',
        emptyHint: 'Escribe algo o mantén pulsado el micrófono para hablar.',
        start: 'Principio de la conversación',
        today: 'Hoy',
        yesterday: 'Ayer',
        typing: '{names} está escribiendo… | {names} están escribiendo…',
        newMessages: '{count} mensaje nuevo | {count} mensajes nuevos',
        members: '{count} persona | {count} personas',
        info: 'Detalles',
        gone: 'Ya no formas parte de esta conversación.',
        overseeing: 'Estás leyendo esta conversación como supervisión. Queda registrado cada vez que la abres.',
        overseeReadOnly: 'En supervisión se lee, no se escribe.',
        cannotWrite: 'No puedes escribir en esta conversación.',
        copilotOffHere: 'El asistente está apagado en esta conversación.',
        callInProgress: 'Hay una llamada en curso aquí.',
        joinCall: 'Entrar',
      },
      message: {
        goToReplied: 'Ir al mensaje respondido',
        earlier: 'Mensaje anterior',
        earlierHint: 'Toca para verlo',
        more: 'Ver más',
        less: 'Ver menos',
        deleted: 'Mensaje borrado',
        edited: 'editado',
        sending: 'Enviando',
        sent: 'Enviado',
        read: 'Leído',
        readBy: 'Leído por {count}',
        failed: 'No se ha podido enviar.',
        offline: 'Sin conexión: se enviará al volver la cobertura.',
        tooLarge: 'El archivo es demasiado grande.',
        retry: 'Reintentar',
        discard: 'Descartar',
        uploading: 'Subiendo',
        reply: 'Responder',
        copy: 'Copiar',
        edit: 'Editar',
        delete: 'Borrar',
        deleteTitle: '¿Borrar este mensaje?',
        deleteHelp: 'Quedará como «mensaje borrado» para todos. El archivo, la nota o la foto se eliminan.',
        openPhoto: 'Ver la foto',
        seenHelp: 'Lo que ve el asistente en la foto',
      },
      composer: {
        tray: {
          camera: 'Cámara',
          gallery: 'Galería',
          video: 'Vídeo',
          document: 'Documento',
          location: 'Ubicación',
        },
        attach: 'Adjuntar',
        placeholderIn: 'Escribir en {where}',
        replyingTo: 'Respondiendo a {name}',
        placeholder: 'Escribe un mensaje',
        send: 'Enviar',
        camera: 'Hacer una foto',
        gallery: 'Foto o vídeo de la galería',
        video: 'Grabar un vídeo',
        document: 'Un documento',
        location: 'Mi ubicación',
        tooLarge: '«{name}» pesa más de {mb} MB y no se puede enviar.',
      },
      voice: {
        speed: 'Velocidad {rate}',
        hold: 'Mantén pulsado para hablar',
        release: 'Suelta para enviar',
        holdHint: 'Mantén pulsado el micrófono mientras hablas y suelta para enviar.',
        slideToCancel: 'Desliza para cancelar',
        releaseToCancel: 'Suelta para cancelar',
        left: 'Quedan {seconds} s',
        play: 'Reproducir',
        pause: 'Pausa',
        cannotPlay: 'Este navegador no puede reproducir la nota. Descárgala para oírla.',
      },
      transcript: {
        pending: 'Transcribiendo…',
        failed: 'No se pudo transcribir.',
        retry: 'Reintentar',
        disabled: {
          provider: 'Esta instancia no tiene transcripción: la nota se oye, pero no hay texto.',
          budget: 'Sin transcripción: se ha agotado el presupuesto de IA de este mes.',
          off: 'Sin transcripción: el asistente está apagado en esta instancia.',
          thread: 'Sin transcripción: el asistente está apagado en esta conversación.',
          error: 'No se pudo transcribir.',
        },
      },
      location: {
        shared: 'Ubicación compartida',
        accuracy: 'Precisión de unos {meters} m',
        denied: 'No has dado permiso para usar la ubicación. Actívalo en los ajustes del navegador.',
        timeout: 'No se ha podido saber dónde estás a tiempo. Prueba al aire libre.',
        unavailable: 'Este dispositivo no puede compartir la ubicación desde aquí.',
      },
      copilot: {
        ask: '¿Qué sacas de esto?',
        found: 'El asistente propone {count} cosa | El asistente propone {count} cosas',
        nothing: 'El asistente no ve nada que apuntar.',
        offInstance: 'El asistente está apagado en esta instancia.',
        budget: 'Se ha agotado el presupuesto de IA del mes: el asistente no transcribe ni propone hasta el mes que viene. Los mensajes y las llamadas funcionan igual.',
        noTranscription: 'Esta instancia no tiene transcripción: el asistente solo lee lo escrito.',
      },
      proposal: {
        summaryHead: 'Resumen de la reunión',
        summaryTitle: 'Resumen',
        summaryNoActions: 'No ha salido nada que apuntar en el ERP.',
        title: 'Acción detectada',
        heard: 'Ha entendido',
        willDo: 'Acción',
        more: 'Ver más',
        less: 'Ver menos',
        accept: 'Confirmar',
        reject: 'Descartar',
        retry: 'Reintentar',
        executing: 'Apuntando…',
        done: 'Apuntado en el ERP',
        failed: 'No se pudo hacer: {reason}',
        effectUnknown: 'No se sabe si llegó a hacerse (tardó demasiado). Compruébalo antes de repetirlo.',
        titleMany: '{count} acciones detectadas',
        where: 'Dónde',
        willChange: 'Qué cambiará',
        changed: 'Qué cambió',
        open: 'Abrir',
        doneTitle: 'Acción realizada',
        doneBy: 'Confirmada por {name} · {when}',
        rejectedTitle: 'Propuesta descartada',
        expiredTitle: 'Propuesta caducada',
        failedTitle: 'No se ha podido realizar la acción.',
        failedWhy: {
          invalid: 'El módulo no ha aceptado los datos: corrígelos y vuelve a intentarlo.',
          gone: 'Lo que había que cambiar ya no existe.',
          conflict: 'Algo ha cambiado mientras tanto: revísalo y vuelve a intentarlo.',
          timeout: 'Tardó demasiado en responder.',
          error: 'Vuelve a intentarlo en un momento.',
        },
        edit: 'Corregir',
        saveEdit: 'Guardar cambios',
        editInvalid: 'Ese valor no vale para esta acción.',
        editedBy: 'Corregida por {name} antes de confirmar',
        echo: {
          pending: 'Ojo: lo mismo se propuso el {when} y sigue sin contestar. Apunta solo una si es la misma cosa.',
          executing: 'Ojo: lo mismo se está apuntando ahora mismo (propuesto el {when}).',
          accepted: 'Ojo: lo mismo ya se apuntó (propuesto el {when}). Apúntalo solo si es otra vez de verdad.',
          rejected: 'Lo mismo se propuso el {when} y se descartó.',
        },
        noPermission: 'No tienes permiso para esto: lo puede apuntar otra persona de la conversación.',
        trigger: {
          silence: 'De la conversación',
          count: 'De la conversación',
          call: 'De la llamada',
          manual: 'Lo pediste tú',
        },
        state: {
          accepted: 'Apuntado por {name}, {when}',
          rejected: 'Descartado por {name}',
          expired: 'Caducó sin respuesta',
          pending: 'Pendiente',
        },
      },
      call: {
        makeHost: 'Hacer anfitrión',
        host: 'Anfitrión',
        youTile: '{name} (tú)',
        thisDevice: 'este dispositivo',
        removedShort: 'Fuera de la llamada',
        speakerButton: 'Altavoz',
        speakerOn: 'Activar el altavoz',
        speakerOff: 'Silenciar el altavoz',
        device: { desktop: 'Ordenador', tablet: 'Tablet', phone: 'Móvil', unknown: 'Dispositivo', companion: '{device} (complementario)' },
        companion: {
          title: 'Ya estás conectado a esta reunión desde otro dispositivo',
          hint: 'Elige cómo quieres entrar desde aquí.',
          fromDevice: 'Ahora mismo estás dentro desde: {device}.',
          join: 'Entrar como dispositivo complementario',
          joinHint: 'Para ver la pantalla o enseñar algo. Entras con el micrófono, el altavoz y la cámara apagados.',
          replace: 'Cambiar a este dispositivo',
          replaceHint: 'Sigues la reunión desde aquí y el otro dispositivo sale.',
          banner: 'Modo complementario: audio y micrófono desactivados para evitar eco.',
          bannerOn: 'Modo complementario: si el otro dispositivo está cerca, puede haber eco.',
        },
        leave: {
          title: '¿Salir o terminar para todos?',
          justMe: 'Salir solo yo',
          justMeHint: 'Los demás siguen hablando.',
          everyone: 'Terminar para todos',
          everyoneHint: 'Se cuelga la llamada de todos.',
          everyoneSure: 'Pulsa otra vez para terminar para todos',
        },
        audio: 'Llamar',
        video: 'Videollamada',
        answer: 'Contestar',
        decline: 'Rechazar',
        hangup: 'Colgar',
        mute: 'Silenciar',
        unmute: 'Activar micro',
        camera: 'Cámara',
        flip: 'Cambiar cámara',
        flipShort: 'Girar',
        blurShort: 'Desenfocar',
        cameraOn: 'Activar cámara',
        cameraOff: 'Apagar cámara',
        more: 'Más',
        answerAudio: 'Contestar sin vídeo',
        participants: 'Participantes',
        inCall: '{count} en la llamada',
        you: 'Tú',
        mutedBadge: 'Silenciado',
        noCamera: 'Sin cámara',
        pin: 'Destacar',
        unpin: 'Quitar destacado',
        fit: 'Ver la imagen entera',
        fill: 'Llenar la pantalla',
        selfHide: 'Ocultar mi vídeo',
        selfShow: 'Mostrar mi vídeo',
        waiting: 'Esperando a que se una…',
        invited: 'avisado',
        declinedShort: 'no puede',
        screen: {
          of: 'Pantalla de {name}',
          youPresent: 'Presentando',
          busyTitle: '{name} está presentando',
          busyHint: 'Si presentas tú, su pantalla deja de compartirse.',
          busyLockedHint: 'Solo quien organiza puede presentar en su lugar. Pídele que deje de compartir.',
          takeOver: 'Presentar en su lugar',
          start: 'Compartir pantalla',
          stop: 'Dejar de compartir la pantalla',
          startShort: 'Pantalla',
          stopShort: 'Dejar de compartir',
          sharing: 'Estás compartiendo tu pantalla',
          badge: 'Pantalla',
        },
        debug: {
          user: 'Usuario',
          device: 'Aparato',
          browser: 'Navegador',
          call: 'Llamada',
          engine: 'Motor de llamadas',
          mic: 'Micro',
          video: 'Vídeo',
          remoteTracks: 'Sus pistas',
          connectionState: 'connectionState',
        },
        panel: {
          debugTab: 'Diagnóstico',
          debugMe: 'Este aparato',
          debugAlone: 'Todavía no hay nadie más conectado a la llamada.',
          debugCopy: 'Copiar diagnóstico',
          settings: 'Ajustes',
          back: 'Volver a la llamada',
          devicesTab: 'Audio y vídeo',
          waiting: 'Esperando para entrar',
          pinned: 'Destacado',
          link: 'Enlace de la reunión',
          micMuted: 'Silenciado: nadie te oye',
          heard: 'Te estamos oyendo',
          speakToTest: 'Habla para probarlo',
          cameraIsOff: 'Apagada: los demás ven tu inicial',
          recording: { title: 'Grabación', on: 'Se está grabando', paused: 'En pausa: ahora no se graba', off: 'No se graba', cancelled: 'Cancelada: se borró lo grabado', pause: 'Pausar', resume: 'Reanudar', cancel: 'Cancelar y borrar', cancelSure: '¿Borrar lo grabado?', onlyOrganizer: 'Solo quien organiza puede pausarla o cancelarla.' },
          blur: 'Desenfocar el fondo',
          blurOn: 'Se ve solo a ti; el fondo, borroso',
          blurOff: 'Se ve lo que hay detrás de ti',
          blurLoading: 'Preparando el desenfoque…',
          nothingToChoose: 'Este aparato solo tiene una cámara, un micrófono y una salida: no hay nada que elegir.',
          muted: 'Silenciado',
          speaking: 'Hablando',
          cameraOnShort: 'con cámara',
          cameraOffShort: 'sin cámara',
        },
        devices: {
          title: 'Cámara, micro y sonido',
          camera: 'Cámara',
          mic: 'Micrófono',
          output: 'Se oye por',
          front: 'Frontal',
          back: 'Trasera',
          numbered: '{kind} {n}',
        },
        connection: {
          good: 'Buena conexión',
          fair: 'Conexión justa',
          poor: 'Conexión débil',
          reconnecting: 'Reconectando…',
          offline: 'Sin conexión',
        },
        notices: {
          reconnect_stuck: 'La conexión se ha cortado varias veces seguidas. Cuando vuelva a ir bien, pulsa Reintentar.',
          retry: 'Reintentar',
          you_are_host: 'Ahora eres quien manda en la reunión: puedes admitir, sacar y terminar para todos.',
          screen_taken: 'Otra persona ha empezado a presentar y has dejado de compartir.',
          mic_switched: 'Se desconectó el micrófono: ahora se usa otro.',
          video_paused: 'Tu vídeo está en pausa: la red no da y se prioriza la voz.',
          resume: 'Reanudar vídeo',
          poor: 'Conexión débil: el vídeo baja de calidad para que se os siga oyendo.',
          offline: 'Sin conexión. La llamada se reanuda sola al volver la red.',
          too_many_for_video: 'Sois demasiados para vídeo: la llamada sigue de audio.',
          speakingMuted: 'Estás silenciado',
          remotePaused: '{name} no manda vídeo ahora mismo',
        },
        speaker: 'Salida',
        minimise: 'Minimizar',
        backToCall: 'Volver a la llamada',
        rejoin: 'Volver a la llamada',
        incomingAudio: 'Te está llamando',
        incomingVideo: 'Videollamada entrante',
        iosHint: 'Si la otra persona usa iPhone, puede tardar en sonar o sonar una sola vez.',
        recording: 'Esta llamada se está grabando',
        recordingShort: 'Grabando',
        pausedShort: 'En pausa',
        recordingUnsupported: 'este navegador no puede grabar tu parte',
        recordingLimit: 'se ha llegado al máximo de grabación',
        uploading: 'subiendo {count} trozos',
        transcribing: 'Transcribiendo la llamada…',
        whatWasSaid: 'Lo que se dijo ({count} intervenciones)',
        voicemailTitle: 'No lo ha cogido. Déjale el recado a {name}.',
        voicemailSent: 'Recado enviado.',
        phase: {
          choose: 'Ya estás dentro desde otro dispositivo',
          replaced: 'La llamada sigue en otro dispositivo',
          removed: 'Te han sacado de la llamada',
          outgoing: 'Llamando…',
          incoming: 'Te está llamando',
          connecting: 'Conectando…',
          connected: 'En llamada',
          reconnecting: 'Reconectando…',
          rejoin: 'Estabas en esta llamada',
          ended: 'Llamada terminada',
          missed: 'No lo ha cogido',
          declined: 'Llamada rechazada',
          busy: 'Está en otra llamada',
          failed: 'No se ha podido llamar',
        },
        peer: {
          connecting: 'conectando',
          reconnecting: 'reconectando',
          failed: 'sin conexión',
          connected: 'conectado',
          new: 'conectando',
        },
        outcome: {
          answered: 'Llamada · {duration}',
          missed: 'Llamada perdida',
          declined: 'Llamada rechazada',
          busy: 'Ocupado',
          voicemail: 'Llamada perdida · dejó una nota de voz',
          failed: 'La llamada no pudo conectar',
        },
        degraded: {
          too_many_for_video: 'Sois demasiados para vídeo en una red móvil: la llamada es de audio.',
        },
        errors: {
          reconnect_stuck: 'No se ha podido volver a entrar: la conexión se corta una y otra vez. Pulsa Reintentar cuando vaya mejor.',
          meeting_over: 'Esta reunión ya ha terminado.',
          meeting_not_started: 'La reunión aún no ha empezado.',
          guest_failed: 'No se ha podido admitir o rechazar a esa persona.',
          recording_control_failed: 'No se ha podido cambiar la grabación.',
          recording_permission: 'La grabación la controla quien organiza.',
          camera_lost: 'La cámara se ha desconectado.',
          mic_lost: 'El micrófono se ha desconectado y no hay otro.',
          removed_from_call: 'Te han sacado de esta llamada.',
          host_permission: 'Eso solo lo puede hacer quien organiza.',
          remove_failed: 'No se ha podido sacar a esa persona.',
          end_failed: 'No se ha podido terminar la llamada.',
          replaced: 'La llamada sigue en otro dispositivo.',
          insecure: 'Las llamadas, el micrófono y la cámara necesitan HTTPS. Entra por la dirección segura del ERP.',
          unsupported: 'Este navegador no puede hacer llamadas.',
          mic_denied: 'No has dado permiso para el micrófono. Actívalo en los ajustes del navegador.',
          mic_missing: 'No se encuentra ningún micrófono.',
          mic_busy: 'Otra aplicación está usando el micrófono.',
          mic_insecure: 'El micrófono necesita HTTPS.',
          mic_unsupported: 'Este navegador no puede usar el micrófono.',
          mic_unknown: 'No se ha podido usar el micrófono.',
          camera_denied: 'Sin permiso para la cámara: la llamada sigue solo con audio.',
          camera_missing: 'No hay cámara: la llamada sigue solo con audio.',
          camera_busy: 'Otra aplicación usa la cámara: la llamada sigue solo con audio.',
          camera_insecure: 'La cámara necesita HTTPS.',
          camera_unsupported: 'Este navegador no puede usar la cámara.',
          camera_unknown: 'No se ha podido usar la cámara: la llamada sigue solo con audio.',
          camera_switch: 'No se ha podido cambiar de cámara.',
          screen_failed: 'No se ha podido compartir la pantalla.',
          webrtc_failed: 'No se ha podido conectar con la otra persona. Comprobad la cobertura.',
          mic_switch: 'No se ha podido cambiar de micrófono.',
          too_many_for_video: 'Sois demasiados para vídeo: la llamada sigue de audio.',
          call_failed: 'No se ha podido hacer la llamada.',
          call_permission: 'No tienes permiso para llamar.',
          nobody_to_call: 'No hay nadie más en esta conversación.',
          too_many_for_audio: 'Sois demasiados para una llamada.',
          call_ended: 'La llamada ya ha terminado.',
          call_full: 'La llamada está llena.',
          not_in_call: 'No estás en esta llamada.',
          device_limit_reached: 'Ya estás en esta llamada desde tres dispositivos, que es el máximo. Sal desde uno de ellos para entrar desde este.',
          plan_limit_reached: 'La llamada no admite a nadie más: se ha llegado al máximo de personas que permite el servicio de llamadas contratado.',
          call_engine_unavailable: 'Las llamadas no están disponibles ahora: el servicio de llamadas no responde. Inténtalo de nuevo en unos minutos; los mensajes siguen funcionando.',
          calls_not_configured: 'Las llamadas no están activadas en esta instalación: falta configurar el servicio de llamadas (IACL Comms). Los mensajes funcionan con normalidad.',
        },
      },
      recording: {
        listen: 'Escuchar la grabación',
        none: 'No hay grabación.',
        purged: 'La grabación ya se ha borrado (se conserva unos días). Lo que se dijo sigue aquí escrito.',
        pieces: '{count} trozos',
      },
      callPage: {
        opening: 'Abriendo la llamada…',
        gone: 'Esta llamada ya ha terminado',
        goneHint: 'Si era para ti, la verás como perdida en la conversación.',
      },
      subject: {
        opening: 'Abriendo la conversación…',
        notFound: 'No hay conversación aquí para ti',
        notFoundHint: 'El registro no existe o no tienes acceso a él.',
      },
      review: {
        title: 'Repaso',
        subtitle: 'Lo que el asistente propuso y nadie ha contestado todavía',
        emptyTitle: 'Nada pendiente',
        emptyHint: 'Las propuestas se contestan en su conversación. Aquí solo llegan las que llevan tiempo esperando.',
        failed: 'No se ha podido cargar el repaso',
      },
      info: {
        rename: 'Cambiar el nombre',
        archive: 'Archivar',
        notifications: 'Mis avisos',
        mute: 'Silenciar esta conversación',
        notifyLevel: 'Avisarme de',
        levels: { all: 'Todo', mentions: 'Solo si me nombran', none: 'Nada' },
        copilot: 'Asistente',
        copilotHelp: 'El asistente lee lo que se dice aquí —mensajes, notas de voz, fotos y llamadas— y propone apuntarlo en el ERP. Nunca escribe nada sin que alguien lo acepte.',
        copilotSwitch: 'El asistente escucha esta conversación',
        copilotOffWarning: 'Todos los de la conversación verán que lo has apagado. Mientras esté apagado no se transcribe ni se propone nada.',
        copilotOffConfirm: 'Apagarlo',
        people: 'Personas ({count})',
        you: 'tú',
        remove: 'Quitar a {name}',
        addPlaceholder: 'Añadir a alguien: escribe su nombre',
        about: 'Conversación sobre: {type}',
        kinds: { direct: 'Conversación privada', group: 'Grupo', subject: 'Conversación de un registro' },
        roles: { owner: 'Creador', admin: 'Administra', member: 'Miembro' },
        leave: { direct: 'Quitar de mi lista', group: 'Salir del grupo', subject: 'Dejar de seguir' },
        leaveWarning: {
          direct: 'Desaparece de tu lista hasta que alguien de los dos vuelva a escribir.',
          group: 'Dejarás de ver este grupo y su historia.',
          subject: 'Dejarás de recibir sus avisos. Podrás volver desde la ficha del registro.',
        },
      },
      push: {
        install: {
          title: 'Instala Radio en la pantalla de inicio',
          body: 'En iPhone los avisos de mensajes y llamadas solo llegan con la aplicación instalada: Compartir → Añadir a pantalla de inicio, y ábrela desde el icono.',
        },
        enable: {
          title: 'Activa los avisos',
          body: 'Para enterarte de mensajes y llamadas con la aplicación cerrada.',
          button: 'Activar',
        },
        denied: {
          title: 'Los avisos están bloqueados',
          body: 'Sin avisos no te enterarás de las llamadas con la aplicación cerrada. Desbloquéalos en los ajustes del navegador para este sitio.',
        },
        iosLimits: 'En iPhone una llamada llega como un aviso con un sonido, una vez: no vibra ni insiste.',
        enabled: 'Avisos activados',
        failed: 'No se han podido activar los avisos.',
      },
      system: {
        group_created: '{by} ha creado el grupo «{title}»',
        group_renamed: '{by} ha cambiado el nombre a «{title}»',
        member_added: '{by} ha añadido a {name}',
        member_removed: '{by} ha quitado a {name}',
        member_left: '{name} ha salido del grupo',
        ai_on: '{by} ha encendido el asistente en esta conversación',
        ai_off: '{by} ha apagado el asistente en esta conversación',
        recording_on: '{by} ha empezado una llamada que se graba. Cada uno graba su propio micrófono.',
        recording_paused: '{by} ha pausado la grabación.',
        recording_resumed: '{by} ha reanudado la grabación.',
        recording_cancelled: '{by} ha cancelado la grabación: se ha borrado lo grabado en esta llamada.',
      },
      errors: {
        self_conversation: 'No puedes abrir una conversación contigo.',
        groups_permission: 'No tienes permiso para crear grupos.',
        oversee_read_only: 'En supervisión se lee, no se escribe.',
        cannot_write: 'No puedes escribir en esta conversación.',
        use_permission: 'No tienes permiso para usar Radio.',
        without_radio: 'Esa persona no tiene Radio. Pide a quien administra el ERP que le dé acceso en su rol.',
        manage_permission: 'No puedes cambiar este grupo.',
        remove_owner: 'No se puede quitar a quien creó el grupo.',
        not_member: 'No estás en esta conversación.',
        not_author: 'Solo quien lo escribió puede hacerlo.',
        not_editable: 'Este mensaje no se puede editar.',
        edit_window: 'Ya ha pasado demasiado tiempo para editarlo.',
        copilot_off: 'El asistente está apagado en esta instancia.',
        copilot_thread: 'El asistente está apagado en esta conversación.',
        copilot_budget: 'Se ha agotado el presupuesto de IA de este mes.',
        effect_unknown: 'Puede que ya se hiciera: compruébalo antes de repetirlo.',
        tool_gone: 'El módulo que hacía esto ya no está instalado.',
        tool_forbidden: 'No tienes permiso para hacer esto.',
        call_permission: 'No tienes permiso para llamar.',
        meetings_permission: 'No tienes permiso para convocar reuniones.',
        nobody_to_call: 'No hay nadie más en esta conversación.',
        too_many_for_audio: 'Sois demasiados para una llamada.',
        call_ended: 'La llamada ya ha terminado.',
        call_full: 'La llamada está llena.',
        not_in_call: 'No estás en esta llamada.',
        device_limit_reached: 'Ya estás en esta llamada desde tres dispositivos, que es el máximo. Sal desde uno de ellos para entrar desde este.',
        plan_limit_reached: 'La llamada no admite a nadie más: se ha llegado al máximo de personas que permite el servicio de llamadas contratado.',
        call_engine_unavailable: 'Las llamadas no están disponibles ahora: el servicio de llamadas no responde. Inténtalo de nuevo en unos minutos; los mensajes siguen funcionando.',
        calls_not_configured: 'Las llamadas no están activadas en esta instalación: falta configurar el servicio de llamadas (IACL Comms). Los mensajes funcionan con normalidad.',
        not_recording: 'Esta llamada no se graba.',
        recording_limit: 'Se ha llegado al máximo de grabación.',
      },
      related: {
        tab: 'Lo que se ha hablado aquí',
        open: 'Hablar aquí',
        openHint: 'Abre la conversación de este registro en Radio',
      },
      dashboard: {
        pending: 'Propuestas del asistente sin contestar',
        pendingHelp: 'Lo que el asistente ha sacado de tus conversaciones y espera a que alguien lo apunte o lo descarte.',
        unread: 'Mensajes sin leer en Radio',
        unreadHelp: 'Mensajes de tus conversaciones que todavía no has visto.',
      },
      notifications: {
        knock: { title: '{name} quiere entrar', message: '{title}' },
        categories: {
          meetingKnock: 'Radio: alguien quiere entrar en una reunión',
          message: 'Radio: mensajes nuevos',
          call: 'Radio: llamadas entrantes',
          missedCall: 'Radio: llamadas perdidas',
          proposal: 'Radio: propuestas del asistente',
          budget: 'Radio: presupuesto de IA agotado',
        },
        ring: {
          title: '{name} te está llamando',
          titleIn: '{name} te está llamando · {where}',
          voice: 'Llamada de voz',
          video: 'Videollamada',
          answer: 'Contestar',
          decline: 'Rechazar',
        },
        message: {
          voice: 'Nota de voz',
          photo: 'Foto',
          photoWithText: 'Foto: {text}',
          video: 'Vídeo',
          videoWithText: 'Vídeo: {text}',
          location: 'Ubicación',
          file: 'Archivo: {name}',
        },
        missed: { title: 'Llamada perdida de {name}', message: 'Te ha llamado por Radio.' },
        proposal: { title: 'El asistente tiene algo que apuntar', message: '{where}' },
        budget: {
          title: 'El asistente de Radio ha gastado el presupuesto del mes',
          message: 'Los mensajes y las llamadas siguen. No se transcribe ni se propone nada hasta el mes que viene o hasta subir el tope (AC.10, {limit} €).',
        },
      },
    },
    assistant: {
      openers: {
        comms: { 'what-said': '¿Qué se ha dicho hoy de la obra?' },
      },
    },
    modules: { comms: 'Radio' },
    permissions: {
      comms: {
        use: 'Hablar por Radio: mensajes, notas de voz y conversaciones de los registros',
        call: 'Hacer llamadas y videollamadas por Radio',
        meetings: 'Convocar reuniones: crearlas, cambiarlas y anularlas (para unirse no hace falta)',
        groups: 'Crear grupos y gestionar quién está en ellos',
        external: 'Meter en una conversación a alguien de fuera (portal de clientes)',
        oversee: 'Leer conversaciones de grupo y de registros sin estar en ellas (queda registrado; nunca las privadas)',
      },
    },
    settings: {
      ns: {
        comms: {
          title: 'Radio',
          subtitle: 'El canal de la empresa, sus llamadas y su asistente',
          groups: { copilot: 'Asistente', recording: 'Grabación de llamadas', calls: 'Llamadas', retention: 'Conservación', limits: 'Límites' },
          copilot: { label: 'Asistente encendido', help: 'Apagado, el canal funciona entero —mensajes, notas y llamadas— pero no se transcribe, describe ni propone nada.' },
          extractAfterMessages: { label: 'Mensajes que hacen leer', help: 'El asistente lee una conversación cuando se queda en silencio, o antes si se acumulan estos mensajes.' },
          extractAfterSilenceSeconds: { label: 'Silencio antes de leer', help: 'Segundos sin mensajes antes de que el asistente lea lo que se ha dicho.' },
          recordCalls: { label: 'Grabar las llamadas', help: 'Cada participante graba su propio micrófono. Todos ven durante toda la llamada que se graba, y la conversación lo dice. Informa antes por escrito a la plantilla (arts. 88-90 LOPDGDD).' },
          recordingRetentionDays: { label: 'Días que se guardan las grabaciones', help: 'Después se borra el audio. Lo que se dijo se queda como texto mientras se guarden los mensajes.' },
          messageRetentionMonths: { label: 'Meses que se guardan los mensajes', help: 'Mensajes, fotos, ubicaciones y transcripciones más antiguos se borran cada noche.' },
          ringSeconds: { label: 'Tiempo de timbre', help: 'Segundos que suena una llamada antes de darla por perdida y ofrecer dejar una nota de voz.' },
          maxVideoParticipants: { label: 'Personas en una videollamada', help: 'Por encima, la videollamada se ofrece como audio. Cinco o seis solo con buena cobertura: cada aparato sube un vídeo por persona y la calidad baja para que quepa.' },
          maxAudioParticipants: { label: 'Personas en una llamada de audio', help: 'El máximo de personas en una misma llamada de audio.' },
          monthlyAiBudgetEur: { label: 'Presupuesto mensual de IA', help: 'Cuando el asistente gasta esto en un mes, para hasta el siguiente y se avisa a los administradores. El canal sigue funcionando. Vacío es sin tope propio; 0 es que no gaste nada.' },
          describePhotos: { label: 'Describir las fotos', help: 'El asistente mira cada foto para que una foto sola pueda acabar en una propuesta.' },
          groupMaxMembers: { label: 'Personas en un grupo', help: 'Por encima de cien es una lista de distribución, no una conversación.' },
          reviewAfterHours: { label: 'Horas hasta pasar al repaso', help: 'Una propuesta sin contestar tanto tiempo aparece también en el Repaso.' },
          proposalExpiryDays: { label: 'Días que dura una propuesta', help: 'Pasado este tiempo, una propuesta sin contestar caduca: lo que se dijo probablemente ya no es noticia.' },
          voiceNoteMaxSeconds: { label: 'Nota de voz más larga', help: 'Más de cinco minutos no los escucha nadie.' },
          recordingMaxMinutes: { label: 'Grabación más larga', help: 'La grabación se para aquí y la llamada lo dice. La llamada sigue.' },
        },
      },
    },
    audit: {
      actions: {
        comms: {
          meeting_created: 'Reunión creada',
          meeting_link_regenerated: 'Enlace de reunión regenerado',
          meeting_link_revoked: 'Enlace de reunión desactivado',
          meeting_cancelled: 'Reunión cancelada',
          meeting_ended: 'Reunión terminada',
          meeting_started: 'Reunión empezada',
          meeting_recording_enabled: 'Grabación de reunión activada',
          meeting_guest_requested: 'Invitado de fuera pidió entrar en una reunión',
          meeting_guest_admitted: 'Invitado de fuera admitido en una reunión',
          meeting_guest_device_added: 'Invitado de fuera añadió otro dispositivo a una reunión',
          meeting_guest_rejected: 'Invitado de fuera rechazado en una reunión',
          meeting_guest_removed: 'Invitado de fuera expulsado de una reunión',
          conversation_overseen: 'Conversación leída en supervisión',
          copilot_on: 'Asistente encendido en una conversación',
          copilot_off: 'Asistente apagado en una conversación',
          proposal_accepted: 'Propuesta del asistente aceptada',
          proposal_edited: 'Propuesta del asistente corregida antes de confirmar',
          proposal_rejected: 'Propuesta del asistente descartada',
          proposal_failed: 'Propuesta del asistente que no se pudo hacer',
          group_created: 'Grupo creado',
          group_renamed: 'Grupo renombrado',
          members_added: 'Personas añadidas a un grupo',
          member_removed: 'Persona quitada de un grupo',
          conversation_archived: 'Conversación archivada',
          conversation_unarchived: 'Conversación recuperada del archivo',
        },
      },
    },
  },

  ca: {
    comms: {
      meet: {
        hostLeft: 'Qui organitza ha sortit. La reunió continua: pots seguir parlant amb els altres.',
        another: {
          title: 'Continuar des d’un altre dispositiu',
          hint: 'Escaneja el codi amb el mòbil o la tauleta: hi entres com a tu, sense tornar a demanar pas, amb el micròfon i l’altaveu apagats.',
          make: 'Mostrar el codi',
          copy: 'Copiar l’enllaç',
          expires: 'Val una sola vegada i caduca en 10 minuts.',
        },
        replaced: { title: 'La reunió continua en una altra pestanya.', hint: 'Has obert l’enllaç en una altra pestanya o finestra, i la reunió hi ha passat.', again: 'Continuar aquí' },
        pageTitle: 'Reunió',
        brandFallback: 'Reunió',
        kicker: 'Reunió per vídeo',
        organizedBy: 'Organitza {name}',
        calendar: { add: 'Afegir al meu calendari:', google: 'Google', outlook: 'Outlook', ics: 'Fitxer per a iPhone o Android (.ics)', details: 'Reunió per vídeo amb {who}. Entra des d’aquest enllaç, sense instal·lar res: {url}' },
        gone: {
          title: 'Aquesta reunió ha finalitzat.',
          hint: 'L’enllaç ja no està actiu. Si creus que és un error, demana’n un de nou a qui et va convidar.',
        },
        removed: { title: 'Ja no ets en aquesta reunió.', hint: 'Qui l’organitza t’ha tret de la sala.' },
        ended: { title: 'La reunió ha acabat.', hint: 'Qui l’organitzava l’ha tancada per a tothom. Pots tancar aquesta pàgina.' },
      left: { title: 'Has sortit de la reunió.', hint: 'Si ha estat sense voler, torna a entrar. Si no, pots tancar aquesta pàgina.', again: 'Tornar a entrar' },
        cameraOff: 'Càmera apagada',
        noPreview: 'Prova la càmera i el micròfon abans d’entrar',
        micTest: 'Micròfon',
        tryDevices: 'Provar càmera i micròfon',
        name: 'El teu nom',
        namePlaceholder: 'Nom i cognom',
        email: 'Correu',
        optional: '(opcional)',
        recordingTitle: 'Aquesta reunió es grava.',
        recordingNotice: 'Aquesta reunió es pot enregistrar. Qui l’organitza decideix quan s’enregistra, i ho veuràs a la pantalla mentre passi.',
        recordingConsent: 'Accepto que es gravi la meva veu durant la reunió.',
        aiConsent: 'Accepto que el que digui es transcrigui i el resumeixi un assistent.',
        requestAccess: 'Sol·licitar accés',
        entry: { title: 'Com vols entrar?', micOn: 'Amb micròfon', micOff: 'Silenciat', cameraOn: 'Amb càmera', cameraOff: 'Sense càmera' },
        requestHint: 'Qui organitza la reunió veurà el teu nom i et deixarà passar.',
        waiting: {
          title: 'Esperant que et deixin entrar…',
          hint: 'Ja saben que ets aquí. No tanquis aquesta pàgina.',
          cancel: 'Cancel·lar la sol·licitud',
        },
        rejected: { title: 'No t’han deixat entrar', hint: 'Pots tornar-ho a demanar d’aquí a un moment.', again: 'Tornar a demanar accés' },
        admitted: { title: 'Ja ets a dins', hint: 'La sala s’obre quan entri qui organitza.' },
        room: { alone: 'Esperant els altres…' },
        chat: { attach: 'Adjuntar una foto, un vídeo o un document', location: 'Enviar la meva ubicació', title: 'Xat', empty: 'Aquí es veuen els missatges i fitxers d’aquesta reunió.' },
        errors: {
          device_link_gone: 'Aquest enllaç ja s’ha fet servir o ha caducat. Demana’n un de nou des de l’altre dispositiu.',
          device_link_failed: 'No s’ha pogut preparar l’enllaç. Torna-ho a provar.',
          request_failed: 'No s’ha pogut enviar. Torna-ho a provar.',
          recording_consent: 'Aquesta reunió es grava: per entrar ho has d’acceptar.',
          removed: 'Et van treure d’aquesta reunió: no hi pots tornar a entrar.',
          too_many: 'Has demanat entrar massa vegades.',
          wait: 'Espera uns segons abans de tornar-ho a demanar.',
          door_busy: 'Hi ha molta gent esperant per entrar. Torna-ho a provar d’aquí a un moment.',
          gone: 'Aquesta reunió ha finalitzat.',
          meeting_over: 'Aquesta reunió ha finalitzat.',
          meeting_not_started: 'La sala encara no està oberta.',
          call_failed: 'No s’ha pogut entrar a la sala. Torna-ho a provar.',
        },
      },
      meetings: {
        admitAll: 'Admetre tothom',
        waitingIn: '{count} esperant a «{title}»',
        title: 'Reunions',
        subtitle: 'Videotrucades amb l’oficina, el camp i gent de fora',
        provider: 'Radio (videotrucada)',
        kicker: 'Reunió',
        new: 'Nova reunió',
        edit: 'Canviar la reunió',
        create: 'Crear i copiar enllaç',
        failed: 'No s’han pogut carregar les teves reunions',
        emptyTitle: 'No tens reunions',
        emptyHint: 'Crea’n una i comparteix l’enllaç: qui no té compte entra des del navegador, sense instal·lar res.',
        notFound: 'Aquesta reunió no existeix o no la pots veure',
        groups: { live: 'Ara', next: 'Properes', past: 'Anteriors' },
        status: { scheduled: 'Programada', live: 'En curs', ended: 'Acabada', cancelled: 'Cancel·lada' },
        guestsCount: '{count} convidat de fora | {count} convidats de fora',
        waitingCount: '{count} esperant',
        recorded: 'Es grava',
        aiOn: 'Assistent',
        organizer: 'Organitza {name}',
        lasted: 'Va durar {minutes} min',
        join: 'Entrar amb vídeo',
        joinLive: 'Entrar a la sala',
        joinAudio: 'Entrar només amb àudio',
        enter: 'Entrar a la reunió',
        backToRoom: 'Tornar a la sala',
        inOtherCall: 'Ets en una altra trucada. Penja-la per entrar.',
        wasCancelled: 'Aquesta reunió es va cancel·lar.',
        wasEnded: 'Aquesta reunió ha acabat.',
        openChat: 'Obrir la conversa de la reunió',
        openMeeting: 'Veure reunió',
        openInCalendar: 'Veure al calendari',
        endForAll: 'Acabar per a tothom',
        endConfirm: 'La trucada s’acaba per a tothom, els convidats surten i l’enllaç deixa de servir.',
        cancel: 'Cancel·lar la reunió',
        cancelConfirm: 'Ningú no hi podrà entrar i l’enllaç deixarà de servir.',
        link: 'Enllaç per a convidats',
        copyLink: 'Copiar enllaç',
        copied: 'Copiat',
        copyFailed: 'No s’ha pogut copiar. Selecciona l’enllaç i copia’l a mà.',
        linkHint: 'Qui l’obri demanarà entrar i tu decideixes si passa.',
        linkHintOpen: 'Sense sala d’espera: qui tingui l’enllaç entra directament.',
        expires: 'Serveix fins al {when}.',
        revoked: 'L’enllaç està desactivat. Ningú de fora no pot entrar.',
        noLink: 'Només qui organitza la reunió pot compartir l’enllaç.',
        noExternal: 'Aquesta reunió no admet gent de fora.',
        regenerate: 'Enllaç nou',
        regenerateConfirm: 'L’enllaç actual deixarà de funcionar. Qui el tingui necessitarà el nou.',
        regenerated: 'Enllaç nou a punt. L’anterior ja no serveix.',
        enableLink: 'Crear enllaç',
        revoke: 'Desactivar enllaç',
        revokeConfirm: 'Ningú més podrà demanar entrar. Qui ja és a dins continua a dins.',
        door: 'Convidats de fora',
        noGuests: 'Encara no ha trucat ningú a la porta.',
        decidedBy: 'per {name}',
        consentRecording: 'gravació',
        consentAi: 'transcripció',
        recording: {
          title: 'Enregistrament',
          loading: 'Buscant l’enregistrament…',
          failed: 'No s’ha pogut carregar l’enregistrament',
          none: 'No hi ha res enregistrat d’aquesta reunió. S’enregistra quan està activat als Ajustos de Ràdio i a la reunió, i algú més hi ha entrat.',
          whole: 'La reunió',
          part: 'Part {n}',
          purged: 'L’àudio ja s’ha esborrat pel termini de conservació; queda el que es va dir.',
          prepare: 'Preparar l’àudio',
          mixing: 'Ajuntant les veus… {done} de {total}',
          mixFailed: 'No s’ha pogut preparar l’àudio',
          speed: 'Velocitat',
          download: 'Descarregar l’àudio',
          transcript: 'El que es va dir',
          transcriptPending: 'Transcrivint… apareixerà aquí d’aquí a uns minuts.',
          transcriptOff: 'Sense transcripció: la IA no està activada.',
        },
        admit: 'Admetre',
        reject: 'Rebutjar',
        admitAnyway: 'Admetre',
        remove: 'Treure',
        removeSure: 'Treure?',
        external: 'Extern',
        waitingBadge: 'Esperant',
        guestStates: {
          requested: 'Esperant',
          admitted: 'Admès',
          connected: 'Connectat',
          rejected: 'Rebutjat',
          left: 'Va sortir',
          removed: 'Expulsat',
          withdrawn: 'Se’n va anar abans d’entrar',
        },
        afterTitle: 'El que va sortir de la reunió',
        summaryPending: 'L’assistent està preparant el resum. Apareixerà aquí i a la conversa.',
        knocks: 'Persones esperant per entrar',
        knock: '{name} vol entrar',
        again: 'ho demana per {count}a vegada',
        moreKnocks: '{count} més esperant',
        fields: {
          title: 'Títol',
          titlePlaceholder: 'Revisió de l’obra amb el proveïdor',
          day: 'Dia',
          start: 'Comença',
          end: 'Acaba',
          internal: 'De l’empresa',
          internalHint: 'Entren directament, sense esperar.',
          searchPeople: 'Cercar algú…',
          guests: 'De fora',
          guestsHint: 'Opcional: per reconèixer-los quan truquin a la porta. L’enllaç es comparteix igualment.',
          guestName: 'Nom',
          guestEmail: 'Correu',
          addGuest: 'Afegir convidat',
          externalAccess: 'Poden entrar persones de fora amb l’enllaç',
          waitingRoom: 'Sala d’espera: jo decideixo qui passa',
          ai: 'Assistent: resum i propostes en acabar',
          recording: 'Gravar la reunió',
          recordingHint: 'S’avisa tothom durant tota la reunió i els de fora ho han d’acceptar per entrar. Només grava si la gravació està activada als ajustos de Radio.',
          noWaitingRoomHint: 'Sense sala d’espera, qualsevol amb l’enllaç entra sense preguntar.',
          inCalendar: 'Es desa al calendari: arriba a l’agenda de cada persona de l’empresa convidada, amb la seva invitació.',
        },
        errors: {
          title: 'Posa-li un títol.',
          when: 'Revisa el dia i les hores.',
          tooLong: 'Una reunió no pot durar més de 12 hores.',
          save: 'No s’ha pogut desar. Torna-ho a provar.',
          decide: 'No s’ha pogut. Torna-ho a provar.',
        },
      },
      title: 'Radio',
      back: 'Tornar al canal',
      retry: 'Tornar-ho a provar',
      help: {
        title: 'Radio',
        subtitle: 'Parlar amb l’oficina i amb el camp, trucar, enviar notes de veu i deixar que l’assistent apunti el que es diu',
      },
      connection: {
        idle: 'Sense connectar',
        connecting: 'Connectant…',
        open: 'Connectat',
        retrying: 'Reconnectant…',
        offline: 'Sense connexió',
      },
      list: {
        emptyTitle: 'Encara no parles amb ningú',
        emptyHint: 'Toca «Nova» i tria una persona. Des de la fitxa d’una obra o d’un lloc també s’obre la seva conversa.',
        failed: 'No s’han pogut carregar les teves converses',
        failedHint: 'Comprova la connexió i torna-ho a provar.',
        noMessages: 'Encara no hi ha missatges',
        you: 'Tu',
        inCall: 'Trucada en curs',
        showArchived: 'Veure les arxivades',
        hideArchived: 'Amagar les arxivades',
        noArchived: 'No hi ha res arxivat.',
        restore: 'Recuperar',
      },
      pane: {
        title: 'Tria una conversa',
        hint: 'O comença’n una de nova. Mantén premut el micròfon per enviar una nota de veu.',
      },
      kinds: {
        text: 'Missatge',
        voice: 'Nota de veu',
        photo: 'Foto',
        video: 'Vídeo',
        file: 'Fitxer',
        location: 'Ubicació',
        system: 'Avís',
        call: 'Trucada',
        proposal: 'Proposta de l’assistent',
        deleted: 'Missatge esborrat',
      },
      subjects: {
        property: 'Emplaçament',
        work: 'Obra',
        project: 'Projecte',
        incident: 'Incidència',
        work_order: 'Ordre de treball',
        customer: 'Client',
        asset: 'Màquina',
        vehicle: 'Vehicle',
        production_order: 'Ordre de fabricació',
        location: 'Magatzem',
        team: 'Equip',
      },
      presence: {
        online: 'En línia',
        offline: 'Desconnectat',
        seen: 'Vist {when}',
        onlineCount: '{count} en línia',
      },
      new: {
        title: 'Nova conversa',
        short: 'Nova',
        person: 'Una persona',
        group: 'Un grup',
        search: 'Cerca per nom',
        typeName: 'Escriu un nom',
        nobody: 'No hi ha ningú amb aquest nom',
        groupName: 'Nom del grup',
        groupPlaceholder: 'La colla, l’oficina, els encarregats…',
        create: 'Crear el grup',
      },
      search: {
        placeholder: 'Cercar a les teves converses',
        inThread: 'Cercar',
        nothing: 'No s’ha trobat res',
        mode: {
          semantic: 'Cercant pel significat',
          text: 'Cercant per les paraules',
        },
      },
      thread: {
        openRecord: 'Veure {kind}',
        tooOld: 'Aquest missatge és massa antic per carregar-lo aquí.',
        loading: 'Carregant…',
        loadFailed: 'No s’ha pogut carregar la conversa',
        emptyTitle: 'Encara no hi ha res aquí',
        emptyHint: 'Escriu alguna cosa o mantén premut el micròfon per parlar.',
        start: 'Inici de la conversa',
        today: 'Avui',
        yesterday: 'Ahir',
        typing: '{names} està escrivint… | {names} estan escrivint…',
        newMessages: '{count} missatge nou | {count} missatges nous',
        members: '{count} persona | {count} persones',
        info: 'Detalls',
        gone: 'Ja no formes part d’aquesta conversa.',
        overseeing: 'Estàs llegint aquesta conversa com a supervisió. Queda registrat cada cop que l’obres.',
        overseeReadOnly: 'En supervisió es llegeix, no s’escriu.',
        cannotWrite: 'No pots escriure en aquesta conversa.',
        copilotOffHere: 'L’assistent està apagat en aquesta conversa.',
        callInProgress: 'Hi ha una trucada en curs aquí.',
        joinCall: 'Entrar',
      },
      message: {
        goToReplied: 'Anar al missatge respost',
        earlier: 'Missatge anterior',
        earlierHint: 'Toca per veure’l',
        more: 'Veure’n més',
        less: 'Veure’n menys',
        deleted: 'Missatge esborrat',
        edited: 'editat',
        sending: 'Enviant',
        sent: 'Enviat',
        read: 'Llegit',
        readBy: 'Llegit per {count}',
        failed: 'No s’ha pogut enviar.',
        offline: 'Sense connexió: s’enviarà quan torni la cobertura.',
        tooLarge: 'El fitxer és massa gran.',
        retry: 'Tornar-ho a provar',
        discard: 'Descartar',
        uploading: 'Pujant',
        reply: 'Respondre',
        copy: 'Copiar',
        edit: 'Editar',
        delete: 'Esborrar',
        deleteTitle: 'Vols esborrar aquest missatge?',
        deleteHelp: 'Quedarà com a «missatge esborrat» per a tothom. El fitxer, la nota o la foto s’eliminen.',
        openPhoto: 'Veure la foto',
        seenHelp: 'El que veu l’assistent a la foto',
      },
      composer: {
        tray: {
          camera: 'Càmera',
          gallery: 'Galeria',
          video: 'Vídeo',
          document: 'Document',
          location: 'Ubicació',
        },
        attach: 'Adjuntar',
        placeholderIn: 'Escriure a {where}',
        replyingTo: 'Responent a {name}',
        placeholder: 'Escriu un missatge',
        send: 'Enviar',
        camera: 'Fer una foto',
        gallery: 'Foto o vídeo de la galeria',
        video: 'Gravar un vídeo',
        document: 'Un document',
        location: 'La meva ubicació',
        tooLarge: '«{name}» pesa més de {mb} MB i no es pot enviar.',
      },
      voice: {
        speed: 'Velocitat {rate}',
        hold: 'Mantén premut per parlar',
        release: 'Deixa anar per enviar',
        holdHint: 'Mantén premut el micròfon mentre parles i deixa’l anar per enviar.',
        slideToCancel: 'Llisca per cancel·lar',
        releaseToCancel: 'Deixa anar per cancel·lar',
        left: 'Queden {seconds} s',
        play: 'Reproduir',
        pause: 'Pausa',
        cannotPlay: 'Aquest navegador no pot reproduir la nota. Descarrega-la per escoltar-la.',
      },
      transcript: {
        pending: 'Transcrivint…',
        failed: 'No s’ha pogut transcriure.',
        retry: 'Tornar-ho a provar',
        disabled: {
          provider: 'Aquesta instància no té transcripció: la nota se sent, però no hi ha text.',
          budget: 'Sense transcripció: s’ha exhaurit el pressupost d’IA d’aquest mes.',
          off: 'Sense transcripció: l’assistent està apagat en aquesta instància.',
          thread: 'Sense transcripció: l’assistent està apagat en aquesta conversa.',
          error: 'No s’ha pogut transcriure.',
        },
      },
      location: {
        shared: 'Ubicació compartida',
        accuracy: 'Precisió d’uns {meters} m',
        denied: 'No has donat permís per fer servir la ubicació. Activa’l a la configuració del navegador.',
        timeout: 'No s’ha pogut saber on ets a temps. Prova-ho a l’aire lliure.',
        unavailable: 'Aquest dispositiu no pot compartir la ubicació des d’aquí.',
      },
      copilot: {
        ask: 'Què en treus, d’això?',
        found: 'L’assistent proposa {count} cosa | L’assistent proposa {count} coses',
        nothing: 'L’assistent no veu res per apuntar.',
        offInstance: 'L’assistent està apagat en aquesta instància.',
        budget: 'S’ha exhaurit el pressupost d’IA del mes: l’assistent no transcriu ni proposa fins al mes que ve. Els missatges i les trucades funcionen igual.',
        noTranscription: 'Aquesta instància no té transcripció: l’assistent només llegeix el que està escrit.',
      },
      proposal: {
        summaryHead: 'Resum de la reunió',
        summaryTitle: 'Resum',
        summaryNoActions: 'No ha sortit res per apuntar a l’ERP.',
        title: 'Acció detectada',
        heard: 'Ha entès',
        willDo: 'Acció',
        more: 'Veure’n més',
        less: 'Veure’n menys',
        accept: 'Confirmar',
        reject: 'Descartar',
        retry: 'Tornar-ho a provar',
        executing: 'Apuntant…',
        done: 'Apuntat a l’ERP',
        failed: 'No s’ha pogut fer: {reason}',
        effectUnknown: 'No se sap si es va arribar a fer (va trigar massa). Comprova-ho abans de repetir-ho.',
        titleMany: '{count} accions detectades',
        where: 'On',
        willChange: 'Què canviarà',
        changed: 'Què va canviar',
        open: 'Obrir',
        doneTitle: 'Acció feta',
        doneBy: 'Confirmada per {name} · {when}',
        rejectedTitle: 'Proposta descartada',
        expiredTitle: 'Proposta caducada',
        failedTitle: 'No s’ha pogut fer l’acció.',
        failedWhy: {
          invalid: 'El mòdul no ha acceptat les dades: corregeix-les i torna-ho a provar.',
          gone: 'El que calia canviar ja no existeix.',
          conflict: 'Alguna cosa ha canviat mentrestant: revisa-ho i torna-ho a provar.',
          timeout: 'Ha trigat massa a respondre.',
          error: 'Torna-ho a provar d’aquí a un moment.',
        },
        edit: 'Corregir',
        saveEdit: 'Desar els canvis',
        editInvalid: 'Aquest valor no val per a aquesta acció.',
        editedBy: 'Corregida per {name} abans de confirmar',
        echo: {
          pending: 'Compte: el mateix es va proposar el {when} i encara no s’ha contestat. Apunta’n només una si és la mateixa cosa.',
          executing: 'Compte: el mateix s’està apuntant ara mateix (proposat el {when}).',
          accepted: 'Compte: el mateix ja es va apuntar (proposat el {when}). Apunta-ho només si és una altra vegada de debò.',
          rejected: 'El mateix es va proposar el {when} i es va descartar.',
        },
        noPermission: 'No tens permís per a això: ho pot apuntar una altra persona de la conversa.',
        trigger: {
          silence: 'De la conversa',
          count: 'De la conversa',
          call: 'De la trucada',
          manual: 'Ho vas demanar tu',
        },
        state: {
          accepted: 'Apuntat per {name}, {when}',
          rejected: 'Descartat per {name}',
          expired: 'Ha caducat sense resposta',
          pending: 'Pendent',
        },
      },
      call: {
        makeHost: 'Fer amfitrió',
        host: 'Amfitrió',
        youTile: '{name} (tu)',
        thisDevice: 'aquest dispositiu',
        removedShort: 'Fora de la trucada',
        speakerButton: 'Altaveu',
        speakerOn: 'Activar l’altaveu',
        speakerOff: 'Silenciar l’altaveu',
        device: { desktop: 'Ordinador', tablet: 'Tauleta', phone: 'Mòbil', unknown: 'Dispositiu', companion: '{device} (complementari)' },
        companion: {
          title: 'Ja estàs connectat a aquesta reunió des d’un altre dispositiu',
          hint: 'Tria com vols entrar des d’aquí.',
          fromDevice: 'Ara mateix hi ets des de: {device}.',
          join: 'Entrar com a dispositiu complementari',
          joinHint: 'Per veure la pantalla o ensenyar alguna cosa. Hi entres amb el micròfon, l’altaveu i la càmera apagats.',
          replace: 'Canviar a aquest dispositiu',
          replaceHint: 'Continues la reunió des d’aquí i l’altre dispositiu en surt.',
          banner: 'Mode complementari: àudio i micròfon desactivats per evitar eco.',
          bannerOn: 'Mode complementari: si l’altre dispositiu és a prop, hi pot haver eco.',
        },
        leave: {
          title: 'Sortir o acabar per a tothom?',
          justMe: 'Sortir només jo',
          justMeHint: 'Els altres continuen parlant.',
          everyone: 'Acabar per a tothom',
          everyoneHint: 'Es penja la trucada de tothom.',
          everyoneSure: 'Torna a prémer per acabar per a tothom',
        },
        audio: 'Trucar',
        video: 'Videotrucada',
        answer: 'Respondre',
        decline: 'Rebutjar',
        hangup: 'Penjar',
        mute: 'Silenciar',
        unmute: 'Activar el micro',
        camera: 'Càmera',
        flip: 'Canviar càmera',
        flipShort: 'Girar',
        blurShort: 'Desenfocar',
        cameraOn: 'Activar càmera',
        cameraOff: 'Apagar càmera',
        more: 'Més',
        answerAudio: 'Contestar sense vídeo',
        participants: 'Participants',
        inCall: '{count} a la trucada',
        you: 'Tu',
        mutedBadge: 'Silenciat',
        noCamera: 'Sense càmera',
        pin: 'Destacar',
        unpin: 'Treure destacat',
        fit: 'Veure la imatge sencera',
        fill: 'Omplir la pantalla',
        selfHide: 'Amagar el meu vídeo',
        selfShow: 'Mostrar el meu vídeo',
        waiting: 'Esperant que s’hi uneixi…',
        invited: 'avisat',
        declinedShort: 'no pot',
        screen: {
          of: 'Pantalla de {name}',
          youPresent: 'Presentant',
          busyTitle: '{name} està presentant',
          busyHint: 'Si presentes tu, la seva pantalla deixa de compartir-se.',
          busyLockedHint: 'Només qui organitza pot presentar en el seu lloc. Demana-li que deixi de compartir.',
          takeOver: 'Presentar en el seu lloc',
          start: 'Compartir pantalla',
          stop: 'Deixar de compartir la pantalla',
          startShort: 'Pantalla',
          stopShort: 'Deixar de compartir',
          sharing: 'Estàs compartint la teva pantalla',
          badge: 'Pantalla',
        },
        debug: {
          user: 'Usuari',
          device: 'Aparell',
          browser: 'Navegador',
          call: 'Trucada',
          engine: 'Motor de trucades',
          mic: 'Micro',
          video: 'Vídeo',
          remoteTracks: 'Les seves pistes',
          connectionState: 'connectionState',
        },
        panel: {
          debugTab: 'Diagnòstic',
          debugMe: 'Aquest aparell',
          debugAlone: 'Encara no hi ha ningú més connectat a la trucada.',
          debugCopy: 'Copiar diagnòstic',
          settings: 'Ajustos',
          back: 'Tornar a la trucada',
          devicesTab: 'Àudio i vídeo',
          waiting: 'Esperant per entrar',
          pinned: 'Destacat',
          link: 'Enllaç de la reunió',
          micMuted: 'Silenciat: ningú no et sent',
          heard: 'T’estem sentint',
          speakToTest: 'Parla per provar-lo',
          cameraIsOff: 'Apagada: els altres veuen la teva inicial',
          recording: { title: 'Enregistrament', on: 'S’està enregistrant', paused: 'En pausa: ara no s’enregistra', off: 'No s’enregistra', cancelled: 'Cancel·lat: s’ha esborrat el que s’havia enregistrat', pause: 'Pausa', resume: 'Reprendre', cancel: 'Cancel·lar i esborrar', cancelSure: 'Esborrar el que s’ha enregistrat?', onlyOrganizer: 'Només qui organitza pot posar-lo en pausa o cancel·lar-lo.' },
          blur: 'Desenfocar el fons',
          blurOn: 'Només se’t veu a tu; el fons, borrós',
          blurOff: 'Es veu el que tens darrere',
          blurLoading: 'Preparant el desenfocament…',
          nothingToChoose: 'Aquest aparell només té una càmera, un micròfon i una sortida: no hi ha res a triar.',
          muted: 'Silenciat',
          speaking: 'Parlant',
          cameraOnShort: 'amb càmera',
          cameraOffShort: 'sense càmera',
        },
        devices: {
          title: 'Càmera, micro i so',
          camera: 'Càmera',
          mic: 'Micròfon',
          output: 'Se sent per',
          front: 'Frontal',
          back: 'Posterior',
          numbered: '{kind} {n}',
        },
        connection: {
          good: 'Bona connexió',
          fair: 'Connexió justa',
          poor: 'Connexió feble',
          reconnecting: 'Reconnectant…',
          offline: 'Sense connexió',
        },
        notices: {
          reconnect_stuck: 'La connexió s’ha tallat diverses vegades seguides. Quan torni a anar bé, prem Tornar-ho a provar.',
          retry: 'Tornar-ho a provar',
          you_are_host: 'Ara ets qui mana a la reunió: pots admetre, treure i acabar per a tothom.',
          screen_taken: 'Una altra persona ha començat a presentar i has deixat de compartir.',
          mic_switched: 'S’ha desconnectat el micròfon: ara se n’usa un altre.',
          video_paused: 'El teu vídeo està en pausa: la xarxa no dona i es prioritza la veu.',
          resume: 'Reprendre el vídeo',
          poor: 'Connexió feble: el vídeo baixa de qualitat perquè us continueu sentint.',
          offline: 'Sense connexió. La trucada es reprèn sola quan torni la xarxa.',
          too_many_for_video: 'Sou massa per a vídeo: la trucada continua d’àudio.',
          speakingMuted: 'Estàs silenciat',
          remotePaused: '{name} no envia vídeo ara mateix',
        },
        speaker: 'Sortida',
        minimise: 'Minimitzar',
        backToCall: 'Tornar a la trucada',
        rejoin: 'Tornar a la trucada',
        incomingAudio: 'T’està trucant',
        incomingVideo: 'Videotrucada entrant',
        iosHint: 'Si l’altra persona fa servir iPhone, pot trigar a sonar o sonar una sola vegada.',
        recording: 'Aquesta trucada s’està gravant',
        recordingShort: 'Gravant',
        pausedShort: 'En pausa',
        recordingUnsupported: 'aquest navegador no pot gravar la teva part',
        recordingLimit: 's’ha arribat al màxim de gravació',
        uploading: 'pujant {count} trossos',
        transcribing: 'Transcrivint la trucada…',
        whatWasSaid: 'El que es va dir ({count} intervencions)',
        voicemailTitle: 'No ho ha agafat. Deixa-li l’encàrrec a {name}.',
        voicemailSent: 'Encàrrec enviat.',
        phase: {
          choose: 'Ja hi ets des d’un altre dispositiu',
          replaced: 'La trucada continua en un altre dispositiu',
          removed: 'T’han tret de la trucada',
          outgoing: 'Trucant…',
          incoming: 'T’està trucant',
          connecting: 'Connectant…',
          connected: 'En trucada',
          reconnecting: 'Reconnectant…',
          rejoin: 'Eres en aquesta trucada',
          ended: 'Trucada acabada',
          missed: 'No ho ha agafat',
          declined: 'Trucada rebutjada',
          busy: 'És en una altra trucada',
          failed: 'No s’ha pogut trucar',
        },
        peer: {
          connecting: 'connectant',
          reconnecting: 'reconnectant',
          failed: 'sense connexió',
          connected: 'connectat',
          new: 'connectant',
        },
        outcome: {
          answered: 'Trucada · {duration}',
          missed: 'Trucada perduda',
          declined: 'Trucada rebutjada',
          busy: 'Ocupat',
          voicemail: 'Trucada perduda · ha deixat una nota de veu',
          failed: 'La trucada no s’ha pogut connectar',
        },
        degraded: {
          too_many_for_video: 'Sou massa per a vídeo en una xarxa mòbil: la trucada és d’àudio.',
        },
        errors: {
          reconnect_stuck: 'No s’ha pogut tornar a entrar: la connexió es talla una vegada i una altra. Prem Tornar-ho a provar quan vagi millor.',
          meeting_over: 'Aquesta reunió ja ha acabat.',
          meeting_not_started: 'La reunió encara no ha començat.',
          guest_failed: 'No s’ha pogut admetre o rebutjar aquesta persona.',
          recording_control_failed: 'No s’ha pogut canviar la gravació.',
          recording_permission: 'La gravació la controla qui organitza.',
          camera_lost: 'La càmera s’ha desconnectat.',
          mic_lost: 'El micròfon s’ha desconnectat i no n’hi ha cap altre.',
          removed_from_call: 'T’han tret d’aquesta trucada.',
          host_permission: 'Això només ho pot fer qui organitza.',
          remove_failed: 'No s’ha pogut treure aquesta persona.',
          end_failed: 'No s’ha pogut acabar la trucada.',
          replaced: 'La trucada continua en un altre dispositiu.',
          insecure: 'Les trucades, el micròfon i la càmera necessiten HTTPS. Entra per l’adreça segura de l’ERP.',
          unsupported: 'Aquest navegador no pot fer trucades.',
          mic_denied: 'No has donat permís per al micròfon. Activa’l a la configuració del navegador.',
          mic_missing: 'No es troba cap micròfon.',
          mic_busy: 'Una altra aplicació està fent servir el micròfon.',
          mic_insecure: 'El micròfon necessita HTTPS.',
          mic_unsupported: 'Aquest navegador no pot fer servir el micròfon.',
          mic_unknown: 'No s’ha pogut fer servir el micròfon.',
          camera_denied: 'Sense permís per a la càmera: la trucada continua només amb àudio.',
          camera_missing: 'No hi ha càmera: la trucada continua només amb àudio.',
          camera_busy: 'Una altra aplicació fa servir la càmera: la trucada continua només amb àudio.',
          camera_insecure: 'La càmera necessita HTTPS.',
          camera_unsupported: 'Aquest navegador no pot fer servir la càmera.',
          camera_unknown: 'No s’ha pogut fer servir la càmera: la trucada continua només amb àudio.',
          camera_switch: 'No s’ha pogut canviar de càmera.',
          screen_failed: 'No s’ha pogut compartir la pantalla.',
          webrtc_failed: 'No s’ha pogut connectar amb l’altra persona. Comproveu la cobertura.',
          mic_switch: 'No s’ha pogut canviar de micròfon.',
          too_many_for_video: 'Sou massa per a vídeo: la trucada continua d’àudio.',
          call_failed: 'No s’ha pogut fer la trucada.',
          call_permission: 'No tens permís per trucar.',
          nobody_to_call: 'No hi ha ningú més en aquesta conversa.',
          too_many_for_audio: 'Sou massa per a una trucada.',
          call_ended: 'La trucada ja s’ha acabat.',
          call_full: 'La trucada és plena.',
          not_in_call: 'No ets en aquesta trucada.',
          device_limit_reached: 'Ja ets en aquesta trucada des de tres dispositius, que és el màxim. Surt des d’un d’ells per entrar des d’aquest.',
          plan_limit_reached: 'La trucada no admet ningú més: s’ha arribat al màxim de persones que permet el servei de trucades contractat.',
          call_engine_unavailable: 'Les trucades no estan disponibles ara: el servei de trucades no respon. Torna-ho a provar d’aquí a uns minuts; els missatges continuen funcionant.',
          calls_not_configured: 'Les trucades no estan activades en aquesta instal·lació: cal configurar el servei de trucades (IACL Comms). Els missatges funcionen amb normalitat.',
        },
      },
      recording: {
        listen: 'Escoltar la gravació',
        none: 'No hi ha gravació.',
        purged: 'La gravació ja s’ha esborrat (es conserva uns dies). El que es va dir continua aquí escrit.',
        pieces: '{count} trossos',
      },
      callPage: {
        opening: 'Obrint la trucada…',
        gone: 'Aquesta trucada ja s’ha acabat',
        goneHint: 'Si era per a tu, la veuràs com a perduda a la conversa.',
      },
      subject: {
        opening: 'Obrint la conversa…',
        notFound: 'Aquí no hi ha cap conversa per a tu',
        notFoundHint: 'El registre no existeix o no hi tens accés.',
      },
      review: {
        title: 'Repàs',
        subtitle: 'El que l’assistent ha proposat i encara no ha respost ningú',
        emptyTitle: 'Res pendent',
        emptyHint: 'Les propostes es responen a la seva conversa. Aquí només arriben les que fa temps que esperen.',
        failed: 'No s’ha pogut carregar el repàs',
      },
      info: {
        rename: 'Canviar el nom',
        archive: 'Arxivar',
        notifications: 'Els meus avisos',
        mute: 'Silenciar aquesta conversa',
        notifyLevel: 'Avisa’m de',
        levels: { all: 'Tot', mentions: 'Només si m’anomenen', none: 'Res' },
        copilot: 'Assistent',
        copilotHelp: 'L’assistent llegeix el que es diu aquí —missatges, notes de veu, fotos i trucades— i proposa apuntar-ho a l’ERP. Mai no escriu res sense que algú ho accepti.',
        copilotSwitch: 'L’assistent escolta aquesta conversa',
        copilotOffWarning: 'Tots els de la conversa veuran que l’has apagat. Mentre estigui apagat no es transcriu ni es proposa res.',
        copilotOffConfirm: 'Apagar-lo',
        people: 'Persones ({count})',
        you: 'tu',
        remove: 'Treure {name}',
        addPlaceholder: 'Afegir algú: escriu el seu nom',
        about: 'Conversa sobre: {type}',
        kinds: { direct: 'Conversa privada', group: 'Grup', subject: 'Conversa d’un registre' },
        roles: { owner: 'Creador', admin: 'Administra', member: 'Membre' },
        leave: { direct: 'Treure de la meva llista', group: 'Sortir del grup', subject: 'Deixar de seguir' },
        leaveWarning: {
          direct: 'Desapareix de la teva llista fins que algun dels dos torni a escriure.',
          group: 'Deixaràs de veure aquest grup i la seva història.',
          subject: 'Deixaràs de rebre’n els avisos. Hi podràs tornar des de la fitxa del registre.',
        },
      },
      push: {
        install: {
          title: 'Instal·la Radio a la pantalla d’inici',
          body: 'A l’iPhone els avisos de missatges i trucades només arriben amb l’aplicació instal·lada: Comparteix → Afegeix a la pantalla d’inici, i obre-la des de la icona.',
        },
        enable: {
          title: 'Activa els avisos',
          body: 'Per assabentar-te de missatges i trucades amb l’aplicació tancada.',
          button: 'Activar',
        },
        denied: {
          title: 'Els avisos estan bloquejats',
          body: 'Sense avisos no t’assabentaràs de les trucades amb l’aplicació tancada. Desbloqueja’ls a la configuració del navegador per a aquest lloc.',
        },
        iosLimits: 'A l’iPhone una trucada arriba com un avís amb un so, una vegada: no vibra ni insisteix.',
        enabled: 'Avisos activats',
        failed: 'No s’han pogut activar els avisos.',
      },
      system: {
        group_created: '{by} ha creat el grup «{title}»',
        group_renamed: '{by} ha canviat el nom a «{title}»',
        member_added: '{by} ha afegit {name}',
        member_removed: '{by} ha tret {name}',
        member_left: '{name} ha sortit del grup',
        ai_on: '{by} ha encès l’assistent en aquesta conversa',
        ai_off: '{by} ha apagat l’assistent en aquesta conversa',
        recording_on: '{by} ha començat una trucada que es grava. Cadascú grava el seu propi micròfon.',
        recording_paused: '{by} ha posat en pausa l’enregistrament.',
        recording_resumed: '{by} ha reprès l’enregistrament.',
        recording_cancelled: '{by} ha cancel·lat l’enregistrament: s’ha esborrat el que s’havia enregistrat en aquesta trucada.',
      },
      errors: {
        self_conversation: 'No pots obrir una conversa amb tu mateix.',
        groups_permission: 'No tens permís per crear grups.',
        oversee_read_only: 'En supervisió es llegeix, no s’escriu.',
        cannot_write: 'No pots escriure en aquesta conversa.',
        use_permission: 'No tens permís per fer servir Radio.',
        without_radio: 'Aquesta persona no té Radio. Demana a qui administra l’ERP que li doni accés al seu rol.',
        manage_permission: 'No pots canviar aquest grup.',
        remove_owner: 'No es pot treure qui va crear el grup.',
        not_member: 'No ets en aquesta conversa.',
        not_author: 'Només qui ho va escriure ho pot fer.',
        not_editable: 'Aquest missatge no es pot editar.',
        edit_window: 'Ja ha passat massa temps per editar-lo.',
        copilot_off: 'L’assistent està apagat en aquesta instància.',
        copilot_thread: 'L’assistent està apagat en aquesta conversa.',
        copilot_budget: 'S’ha exhaurit el pressupost d’IA d’aquest mes.',
        effect_unknown: 'Potser ja s’ha fet: comprova-ho abans de repetir-ho.',
        tool_gone: 'El mòdul que feia això ja no està instal·lat.',
        tool_forbidden: 'No tens permís per fer això.',
        call_permission: 'No tens permís per trucar.',
        meetings_permission: 'No tens permís per convocar reunions.',
        nobody_to_call: 'No hi ha ningú més en aquesta conversa.',
        too_many_for_audio: 'Sou massa per a una trucada.',
        call_ended: 'La trucada ja s’ha acabat.',
        call_full: 'La trucada és plena.',
        not_in_call: 'No ets en aquesta trucada.',
        device_limit_reached: 'Ja ets en aquesta trucada des de tres dispositius, que és el màxim. Surt des d’un d’ells per entrar des d’aquest.',
        plan_limit_reached: 'La trucada no admet ningú més: s’ha arribat al màxim de persones que permet el servei de trucades contractat.',
        call_engine_unavailable: 'Les trucades no estan disponibles ara: el servei de trucades no respon. Torna-ho a provar d’aquí a uns minuts; els missatges continuen funcionant.',
        calls_not_configured: 'Les trucades no estan activades en aquesta instal·lació: cal configurar el servei de trucades (IACL Comms). Els missatges funcionen amb normalitat.',
        not_recording: 'Aquesta trucada no es grava.',
        recording_limit: 'S’ha arribat al màxim de gravació.',
      },
      related: {
        tab: 'El que s’ha parlat aquí',
        open: 'Parlar aquí',
        openHint: 'Obre la conversa d’aquest registre a Radio',
      },
      dashboard: {
        pending: 'Propostes de l’assistent sense respondre',
        pendingHelp: 'El que l’assistent ha tret de les teves converses i espera que algú ho apunti o ho descarti.',
        unread: 'Missatges sense llegir a Radio',
        unreadHelp: 'Missatges de les teves converses que encara no has vist.',
      },
      notifications: {
        knock: { title: '{name} vol entrar', message: '{title}' },
        categories: {
          meetingKnock: 'Radio: algú vol entrar en una reunió',
          message: 'Radio: missatges nous',
          call: 'Radio: trucades entrants',
          missedCall: 'Radio: trucades perdudes',
          proposal: 'Radio: propostes de l’assistent',
          budget: 'Radio: pressupost d’IA exhaurit',
        },
        ring: {
          title: '{name} t’està trucant',
          titleIn: '{name} t’està trucant · {where}',
          voice: 'Trucada de veu',
          video: 'Videotrucada',
          answer: 'Respondre',
          decline: 'Rebutjar',
        },
        message: {
          voice: 'Nota de veu',
          photo: 'Foto',
          photoWithText: 'Foto: {text}',
          video: 'Vídeo',
          videoWithText: 'Vídeo: {text}',
          location: 'Ubicació',
          file: 'Fitxer: {name}',
        },
        missed: { title: 'Trucada perduda de {name}', message: 'T’ha trucat per Radio.' },
        proposal: { title: 'L’assistent té alguna cosa per apuntar', message: '{where}' },
        budget: {
          title: 'L’assistent de Radio ha gastat el pressupost del mes',
          message: 'Els missatges i les trucades continuen. No es transcriu ni es proposa res fins al mes que ve o fins que s’apugi el límit (AC.10, {limit} €).',
        },
      },
    },
    assistant: {
      openers: {
        comms: { 'what-said': 'Què s’ha dit avui de l’obra?' },
      },
    },
    modules: { comms: 'Radio' },
    permissions: {
      comms: {
        use: 'Parlar per Radio: missatges, notes de veu i converses dels registres',
        call: 'Fer trucades i videotrucades per Radio',
        meetings: 'Convocar reunions: crear-les, canviar-les i anul·lar-les (per unir-s’hi no cal)',
        groups: 'Crear grups i gestionar qui hi és',
        external: 'Afegir a una conversa algú de fora (portal de clients)',
        oversee: 'Llegir converses de grup i de registres sense ser-hi (queda registrat; mai les privades)',
      },
    },
    settings: {
      ns: {
        comms: {
          title: 'Radio',
          subtitle: 'El canal de l’empresa, les seves trucades i el seu assistent',
          groups: { copilot: 'Assistent', recording: 'Gravació de trucades', calls: 'Trucades', retention: 'Conservació', limits: 'Límits' },
          copilot: { label: 'Assistent encès', help: 'Apagat, el canal funciona sencer —missatges, notes i trucades— però no es transcriu, descriu ni proposa res.' },
          extractAfterMessages: { label: 'Missatges que fan llegir', help: 'L’assistent llegeix una conversa quan es queda en silenci, o abans si s’acumulen aquests missatges.' },
          extractAfterSilenceSeconds: { label: 'Silenci abans de llegir', help: 'Segons sense missatges abans que l’assistent llegeixi el que s’ha dit.' },
          recordCalls: { label: 'Gravar les trucades', help: 'Cada participant grava el seu propi micròfon. Tothom veu durant tota la trucada que es grava, i la conversa ho diu. Informa abans per escrit la plantilla (arts. 88-90 LOPDGDD).' },
          recordingRetentionDays: { label: 'Dies que es guarden les gravacions', help: 'Després s’esborra l’àudio. El que es va dir es queda com a text mentre es guardin els missatges.' },
          messageRetentionMonths: { label: 'Mesos que es guarden els missatges', help: 'Els missatges, fotos, ubicacions i transcripcions més antics s’esborren cada nit.' },
          ringSeconds: { label: 'Temps de timbre', help: 'Segons que sona una trucada abans de donar-la per perduda i oferir deixar una nota de veu.' },
          maxVideoParticipants: { label: 'Persones en una videotrucada', help: 'Per sobre, la videotrucada s’ofereix com a àudio. Cinc o sis només amb bona cobertura: cada aparell puja un vídeo per persona i la qualitat baixa perquè hi càpiga.' },
          maxAudioParticipants: { label: 'Persones en una trucada d’àudio', help: 'El màxim de persones en una mateixa trucada d’àudio.' },
          monthlyAiBudgetEur: { label: 'Pressupost mensual d’IA', help: 'Quan l’assistent gasta això en un mes, s’atura fins al següent i s’avisa els administradors. El canal continua funcionant. Buit vol dir sense límit propi; 0 vol dir que no gasti res.' },
          describePhotos: { label: 'Descriure les fotos', help: 'L’assistent mira cada foto perquè una foto sola pugui acabar en una proposta.' },
          groupMaxMembers: { label: 'Persones en un grup', help: 'Per sobre de cent és una llista de distribució, no una conversa.' },
          reviewAfterHours: { label: 'Hores fins a passar al repàs', help: 'Una proposta sense respondre tant de temps també apareix al Repàs.' },
          proposalExpiryDays: { label: 'Dies que dura una proposta', help: 'Passat aquest temps, una proposta sense respondre caduca: el que es va dir probablement ja no és notícia.' },
          voiceNoteMaxSeconds: { label: 'Nota de veu més llarga', help: 'Més de cinc minuts no els escolta ningú.' },
          recordingMaxMinutes: { label: 'Gravació més llarga', help: 'La gravació s’atura aquí i la trucada ho diu. La trucada continua.' },
        },
      },
    },
    audit: {
      actions: {
        comms: {
          meeting_created: 'Reunió creada',
          meeting_link_regenerated: 'Enllaç de reunió regenerat',
          meeting_link_revoked: 'Enllaç de reunió desactivat',
          meeting_cancelled: 'Reunió cancel·lada',
          meeting_ended: 'Reunió acabada',
          meeting_started: 'Reunió començada',
          meeting_recording_enabled: 'Gravació de reunió activada',
          meeting_guest_requested: 'Convidat de fora va demanar entrar en una reunió',
          meeting_guest_admitted: 'Convidat de fora admès en una reunió',
          meeting_guest_device_added: 'Convidat de fora va afegir un altre dispositiu a una reunió',
          meeting_guest_rejected: 'Convidat de fora rebutjat en una reunió',
          meeting_guest_removed: 'Convidat de fora expulsat d’una reunió',
          conversation_overseen: 'Conversa llegida en supervisió',
          copilot_on: 'Assistent encès en una conversa',
          copilot_off: 'Assistent apagat en una conversa',
          proposal_accepted: 'Proposta de l’assistent acceptada',
          proposal_edited: 'Proposta de l’assistent corregida abans de confirmar',
          proposal_rejected: 'Proposta de l’assistent descartada',
          proposal_failed: 'Proposta de l’assistent que no s’ha pogut fer',
          group_created: 'Grup creat',
          group_renamed: 'Grup reanomenat',
          members_added: 'Persones afegides a un grup',
          member_removed: 'Persona treta d’un grup',
          conversation_archived: 'Conversa arxivada',
          conversation_unarchived: 'Conversa recuperada de l’arxiu',
        },
      },
    },
  },

  en: {
    comms: {
      meet: {
        hostLeft: 'The organiser has left. The meeting goes on: you can keep talking with the others.',
        another: {
          title: 'Continue on another device',
          hint: 'Scan the code with your phone or tablet: you join as yourself, without knocking again, with the microphone and speaker off.',
          make: 'Show the code',
          copy: 'Copy the link',
          expires: 'It works once and expires in 10 minutes.',
        },
        replaced: { title: 'The meeting continues in another tab.', hint: 'You opened the link in another tab or window, and the meeting moved there.', again: 'Continue here' },
        pageTitle: 'Meeting',
        brandFallback: 'Meeting',
        kicker: 'Video meeting',
        organizedBy: 'Hosted by {name}',
        calendar: { add: 'Add to my calendar:', google: 'Google', outlook: 'Outlook', ics: 'File for iPhone or Android (.ics)', details: 'Video meeting with {who}. Join from this link, nothing to install: {url}' },
        gone: {
          title: 'This meeting has ended.',
          hint: 'The link is no longer active. If you think this is a mistake, ask whoever invited you for a new one.',
        },
        removed: { title: 'You are no longer in this meeting.', hint: 'The host removed you from the room.' },
        ended: { title: 'The meeting has ended.', hint: 'The host closed it for everyone. You can close this page.' },
      left: { title: 'You left the meeting.', hint: 'If it was by mistake, join again. Otherwise you can close this page.', again: 'Join again' },
        cameraOff: 'Camera off',
        noPreview: 'Check your camera and microphone before joining',
        micTest: 'Microphone',
        tryDevices: 'Test camera and microphone',
        name: 'Your name',
        namePlaceholder: 'First and last name',
        email: 'Email',
        optional: '(optional)',
        recordingTitle: 'This meeting is recorded.',
        recordingNotice: 'This meeting may be recorded. The organizer decides when, and you will see it on screen while it happens.',
        recordingConsent: 'I agree to my voice being recorded during the meeting.',
        aiConsent: 'I agree to what I say being transcribed and summarised by an assistant.',
        requestAccess: 'Ask to join',
        entry: { title: 'How do you want to join?', micOn: 'With microphone', micOff: 'Muted', cameraOn: 'With camera', cameraOff: 'Camera off' },
        requestHint: 'The host will see your name and let you in.',
        waiting: { title: 'Waiting to be let in…', hint: 'They know you are here. Keep this page open.', cancel: 'Cancel the request' },
        rejected: { title: 'You were not let in', hint: 'You can ask again in a moment.', again: 'Ask to join again' },
        admitted: { title: 'You are in', hint: 'The room opens as soon as the host joins.' },
        room: { alone: 'Waiting for the others…' },
        chat: { attach: 'Attach a photo, a video or a document', location: 'Send my location', title: 'Chat', empty: 'Messages and files from this meeting show up here.' },
        errors: {
          device_link_gone: 'This link has already been used or has expired. Ask for a new one from the other device.',
          device_link_failed: 'The link could not be prepared. Try again.',
          request_failed: 'It could not be sent. Please try again.',
          recording_consent: 'This meeting is recorded: you have to accept it to join.',
          removed: 'You were removed from this meeting and cannot join again.',
          too_many: 'You have asked to join too many times.',
          wait: 'Wait a few seconds before asking again.',
          door_busy: 'A lot of people are waiting to get in. Try again in a moment.',
          gone: 'This meeting has ended.',
          meeting_over: 'This meeting has ended.',
          meeting_not_started: 'The room is not open yet.',
          call_failed: 'Could not join the room. Please try again.',
        },
      },
      meetings: {
        admitAll: 'Admit all',
        waitingIn: '{count} waiting in “{title}”',
        title: 'Meetings',
        subtitle: 'Video calls with the office, the field and people outside the company',
        provider: 'Radio (video call)',
        kicker: 'Meeting',
        new: 'New meeting',
        edit: 'Change the meeting',
        create: 'Create and copy link',
        failed: 'Your meetings could not be loaded',
        emptyTitle: 'You have no meetings',
        emptyHint: 'Create one and share the link: people without an account join from their browser, with nothing to install.',
        notFound: 'This meeting does not exist or you cannot see it',
        groups: { live: 'Now', next: 'Upcoming', past: 'Earlier' },
        status: { scheduled: 'Scheduled', live: 'Live', ended: 'Ended', cancelled: 'Cancelled' },
        guestsCount: '{count} external guest | {count} external guests',
        waitingCount: '{count} waiting',
        recorded: 'Recorded',
        aiOn: 'Assistant',
        organizer: 'Hosted by {name}',
        lasted: 'Lasted {minutes} min',
        join: 'Join with video',
        joinLive: 'Join the room',
        joinAudio: 'Join with audio only',
        enter: 'Join the meeting',
        backToRoom: 'Back to the room',
        inOtherCall: 'You are in another call. Hang up to join.',
        wasCancelled: 'This meeting was cancelled.',
        wasEnded: 'This meeting has ended.',
        openChat: 'Open the meeting conversation',
        openMeeting: 'View meeting',
        openInCalendar: 'View in the calendar',
        endForAll: 'End for everyone',
        endConfirm: 'The call ends for everyone, guests leave and the link stops working.',
        cancel: 'Cancel the meeting',
        cancelConfirm: 'Nobody will be able to join and the link will stop working.',
        link: 'Guest link',
        copyLink: 'Copy link',
        copied: 'Copied',
        copyFailed: 'Could not copy. Select the link and copy it by hand.',
        linkHint: 'Whoever opens it asks to join and you decide who gets in.',
        linkHintOpen: 'No waiting room: anyone with the link goes straight in.',
        expires: 'Valid until {when}.',
        revoked: 'The link is disabled. Nobody from outside can join.',
        noLink: 'Only the host can share the link.',
        noExternal: 'This meeting does not accept people from outside.',
        regenerate: 'New link',
        regenerateConfirm: 'The current link will stop working. Anyone who has it will need the new one.',
        regenerated: 'New link ready. The old one no longer works.',
        enableLink: 'Create link',
        revoke: 'Disable link',
        revokeConfirm: 'Nobody else will be able to ask to join. People already inside stay inside.',
        door: 'External guests',
        noGuests: 'Nobody has knocked yet.',
        decidedBy: 'by {name}',
        consentRecording: 'recording',
        consentAi: 'transcription',
        recording: {
          title: 'Recording',
          loading: 'Looking for the recording…',
          failed: 'The recording could not be loaded',
          none: 'Nothing was recorded in this meeting. It records when it is on in Radio settings and in the meeting, and someone else has joined.',
          whole: 'The meeting',
          part: 'Part {n}',
          purged: 'The audio was deleted after the retention period; what was said remains.',
          prepare: 'Prepare the audio',
          mixing: 'Putting the voices together… {done} of {total}',
          mixFailed: 'The audio could not be prepared',
          speed: 'Speed',
          download: 'Download the audio',
          transcript: 'What was said',
          transcriptPending: 'Transcribing… it will appear here in a few minutes.',
          transcriptOff: 'No transcript: AI is not enabled.',
        },
        admit: 'Admit',
        reject: 'Reject',
        admitAnyway: 'Admit',
        remove: 'Remove',
        removeSure: 'Remove?',
        external: 'External',
        waitingBadge: 'Waiting',
        guestStates: {
          requested: 'Waiting',
          admitted: 'Admitted',
          connected: 'Connected',
          rejected: 'Rejected',
          left: 'Left',
          removed: 'Removed',
          withdrawn: 'Left before joining',
        },
        afterTitle: 'What came out of the meeting',
        summaryPending: 'The assistant is preparing the summary. It will show up here and in the conversation.',
        knocks: 'People waiting to join',
        knock: '{name} wants to join',
        again: 'asking for the {count}th time',
        moreKnocks: '{count} more waiting',
        fields: {
          title: 'Title',
          titlePlaceholder: 'Site review with the supplier',
          day: 'Day',
          start: 'Starts',
          end: 'Ends',
          internal: 'From the company',
          internalHint: 'They go straight in, no waiting.',
          searchPeople: 'Search for someone…',
          guests: 'From outside',
          guestsHint: 'Optional: to recognise them when they knock. The link is shared either way.',
          guestName: 'Name',
          guestEmail: 'Email',
          addGuest: 'Add guest',
          externalAccess: 'People from outside can join with the link',
          waitingRoom: 'Waiting room: I decide who gets in',
          ai: 'Assistant: summary and proposals at the end',
          recording: 'Record the meeting',
          recordingHint: 'Everyone is told for the whole meeting and outside guests have to accept it to join. It only records if recording is enabled in Radio settings.',
          noWaitingRoomHint: 'Without a waiting room, anyone with the link joins without asking.',
          inCalendar: 'Saved to the calendar: it lands in the agenda of everyone from the company you invite, with their invitation.',
        },
        errors: {
          title: 'Give it a title.',
          when: 'Check the day and times.',
          tooLong: 'A meeting cannot last more than 12 hours.',
          save: 'Could not save. Please try again.',
          decide: 'That did not work. Please try again.',
        },
      },
      title: 'Radio',
      back: 'Back to the channel',
      retry: 'Try again',
      help: {
        title: 'Radio',
        subtitle: 'Talk to the office and the field, call, send voice notes, and let the assistant record what is said',
      },
      connection: {
        idle: 'Not connected',
        connecting: 'Connecting…',
        open: 'Connected',
        retrying: 'Reconnecting…',
        offline: 'No connection',
      },
      list: {
        emptyTitle: 'You are not talking to anybody yet',
        emptyHint: 'Tap «New» and pick a person. A job or a site also opens its own conversation from its file.',
        failed: 'Your conversations could not be loaded',
        failedHint: 'Check your connection and try again.',
        noMessages: 'No messages yet',
        you: 'You',
        inCall: 'Call in progress',
        showArchived: 'Show archived',
        hideArchived: 'Hide archived',
        noArchived: 'Nothing archived.',
        restore: 'Restore',
      },
      pane: {
        title: 'Pick a conversation',
        hint: 'Or start a new one. Hold the microphone to send a voice note.',
      },
      kinds: {
        text: 'Message',
        voice: 'Voice note',
        photo: 'Photo',
        video: 'Video',
        file: 'File',
        location: 'Location',
        system: 'Notice',
        call: 'Call',
        proposal: 'Assistant proposal',
        deleted: 'Deleted message',
      },
      subjects: {
        property: 'Site',
        work: 'Job',
        project: 'Project',
        incident: 'Incident',
        work_order: 'Work order',
        customer: 'Customer',
        asset: 'Machine',
        vehicle: 'Vehicle',
        production_order: 'Production order',
        location: 'Warehouse',
        team: 'Team',
      },
      presence: {
        online: 'Online',
        offline: 'Offline',
        seen: 'Seen {when}',
        onlineCount: '{count} online',
      },
      new: {
        title: 'New conversation',
        short: 'New',
        person: 'A person',
        group: 'A group',
        search: 'Search by name',
        typeName: 'Type a name',
        nobody: 'Nobody by that name',
        groupName: 'Group name',
        groupPlaceholder: 'The crew, the office, the foremen…',
        create: 'Create the group',
      },
      search: {
        placeholder: 'Search your conversations',
        inThread: 'Search',
        nothing: 'Nothing found',
        mode: {
          semantic: 'Searching by meaning',
          text: 'Searching by words',
        },
      },
      thread: {
        openRecord: 'Open {kind}',
        tooOld: 'That message is too old to load here.',
        loading: 'Loading…',
        loadFailed: 'The conversation could not be loaded',
        emptyTitle: 'Nothing here yet',
        emptyHint: 'Write something, or hold the microphone to talk.',
        start: 'Start of the conversation',
        today: 'Today',
        yesterday: 'Yesterday',
        typing: '{names} is typing… | {names} are typing…',
        newMessages: '{count} new message | {count} new messages',
        members: '{count} person | {count} people',
        info: 'Details',
        gone: 'You are no longer part of this conversation.',
        overseeing: 'You are reading this conversation as oversight. Every time you open it is recorded.',
        overseeReadOnly: 'Oversight reads, it does not write.',
        cannotWrite: 'You cannot write in this conversation.',
        copilotOffHere: 'The assistant is off in this conversation.',
        callInProgress: 'There is a call in progress here.',
        joinCall: 'Join',
      },
      message: {
        goToReplied: 'Go to the replied message',
        earlier: 'Earlier message',
        earlierHint: 'Tap to see it',
        more: 'Show more',
        less: 'Show less',
        deleted: 'Deleted message',
        edited: 'edited',
        sending: 'Sending',
        sent: 'Sent',
        read: 'Read',
        readBy: 'Read by {count}',
        failed: 'It could not be sent.',
        offline: 'No connection: it will go when the signal is back.',
        tooLarge: 'The file is too large.',
        retry: 'Try again',
        discard: 'Discard',
        uploading: 'Uploading',
        reply: 'Reply',
        copy: 'Copy',
        edit: 'Edit',
        delete: 'Delete',
        deleteTitle: 'Delete this message?',
        deleteHelp: 'It will show as «deleted message» for everybody. The file, the voice note or the photo are removed.',
        openPhoto: 'See the photo',
        seenHelp: 'What the assistant sees in the photo',
      },
      composer: {
        tray: {
          camera: 'Camera',
          gallery: 'Gallery',
          video: 'Video',
          document: 'Document',
          location: 'Location',
        },
        attach: 'Attach',
        placeholderIn: 'Write in {where}',
        replyingTo: 'Replying to {name}',
        placeholder: 'Write a message',
        send: 'Send',
        camera: 'Take a photo',
        gallery: 'Photo or video from the gallery',
        video: 'Record a video',
        document: 'A document',
        location: 'My location',
        tooLarge: '«{name}» is over {mb} MB and cannot be sent.',
      },
      voice: {
        speed: 'Speed {rate}',
        hold: 'Hold to talk',
        release: 'Release to send',
        holdHint: 'Keep the microphone pressed while you talk and release to send.',
        slideToCancel: 'Slide to cancel',
        releaseToCancel: 'Release to cancel',
        left: '{seconds} s left',
        play: 'Play',
        pause: 'Pause',
        cannotPlay: 'This browser cannot play the note. Download it to listen.',
      },
      transcript: {
        pending: 'Transcribing…',
        failed: 'It could not be transcribed.',
        retry: 'Try again',
        disabled: {
          provider: 'This instance has no transcription: the note plays, but there is no text.',
          budget: 'No transcription: this month\'s AI budget has run out.',
          off: 'No transcription: the assistant is off on this instance.',
          thread: 'No transcription: the assistant is off in this conversation.',
          error: 'It could not be transcribed.',
        },
      },
      location: {
        shared: 'Shared location',
        accuracy: 'Accurate to about {meters} m',
        denied: 'You have not allowed location. Enable it in the browser settings.',
        timeout: 'Your position could not be found in time. Try outdoors.',
        unavailable: 'This device cannot share its location from here.',
      },
      copilot: {
        ask: 'What do you make of this?',
        found: 'The assistant proposes {count} thing | The assistant proposes {count} things',
        nothing: 'The assistant sees nothing to record.',
        offInstance: 'The assistant is off on this instance.',
        budget: 'This month\'s AI budget has run out: the assistant does not transcribe or propose until next month. Messages and calls work as usual.',
        noTranscription: 'This instance has no transcription: the assistant only reads what is written.',
      },
      proposal: {
        summaryHead: 'Meeting summary',
        summaryTitle: 'Summary',
        summaryNoActions: 'Nothing came up to record in the ERP.',
        title: 'Action detected',
        heard: 'Understood',
        willDo: 'Action',
        more: 'See more',
        less: 'See less',
        accept: 'Confirm',
        reject: 'Dismiss',
        retry: 'Try again',
        executing: 'Recording…',
        done: 'Recorded in the ERP',
        failed: 'It could not be done: {reason}',
        effectUnknown: 'It is not known whether it happened (it took too long). Check before repeating it.',
        titleMany: '{count} actions detected',
        where: 'Where',
        willChange: 'What will change',
        changed: 'What changed',
        open: 'Open',
        doneTitle: 'Action carried out',
        doneBy: 'Confirmed by {name} · {when}',
        rejectedTitle: 'Proposal dismissed',
        expiredTitle: 'Proposal expired',
        failedTitle: 'The action could not be carried out.',
        failedWhy: {
          invalid: 'The module did not accept the data: correct it and try again.',
          gone: 'What had to change no longer exists.',
          conflict: 'Something changed in the meantime: check it and try again.',
          timeout: 'It took too long to answer.',
          error: 'Try again in a moment.',
        },
        edit: 'Correct',
        saveEdit: 'Save changes',
        editInvalid: 'That value is not valid for this action.',
        editedBy: 'Corrected by {name} before confirming',
        echo: {
          pending: 'Careful: the same thing was proposed on {when} and is still unanswered. Record only one if it is the same thing.',
          executing: 'Careful: the same thing is being recorded right now (proposed on {when}).',
          accepted: 'Careful: the same thing was already recorded (proposed on {when}). Record it only if it really happened again.',
          rejected: 'The same thing was proposed on {when} and dismissed.',
        },
        noPermission: 'You are not allowed to do this: somebody else in the conversation can record it.',
        trigger: {
          silence: 'From the conversation',
          count: 'From the conversation',
          call: 'From the call',
          manual: 'You asked',
        },
        state: {
          accepted: 'Recorded by {name}, {when}',
          rejected: 'Dismissed by {name}',
          expired: 'Expired with no answer',
          pending: 'Pending',
        },
      },
      call: {
        makeHost: 'Make host',
        host: 'Host',
        youTile: '{name} (you)',
        thisDevice: 'this device',
        removedShort: 'Removed from the call',
        speakerButton: 'Speaker',
        speakerOn: 'Turn the speaker on',
        speakerOff: 'Mute the speaker',
        device: { desktop: 'Computer', tablet: 'Tablet', phone: 'Phone', unknown: 'Device', companion: '{device} (companion)' },
        companion: {
          title: 'You are already in this meeting from another device',
          hint: 'Choose how to join from here.',
          fromDevice: 'Right now you are in from: {device}.',
          join: 'Join as a companion device',
          joinHint: 'To see the screen or show something. You join with the microphone, speaker and camera off.',
          replace: 'Switch to this device',
          replaceHint: 'You continue the meeting here and the other device leaves.',
          banner: 'Companion mode: audio and microphone off to avoid echo.',
          bannerOn: 'Companion mode: if the other device is nearby, there may be echo.',
        },
        leave: {
          title: 'Leave, or end for everyone?',
          justMe: 'Just leave',
          justMeHint: 'Everyone else keeps talking.',
          everyone: 'End for everyone',
          everyoneHint: 'The call is hung up for everybody.',
          everyoneSure: 'Tap again to end for everyone',
        },
        audio: 'Call',
        video: 'Video call',
        answer: 'Answer',
        decline: 'Decline',
        hangup: 'Hang up',
        mute: 'Mute',
        unmute: 'Unmute',
        camera: 'Camera',
        flip: 'Switch camera',
        flipShort: 'Flip',
        blurShort: 'Blur',
        cameraOn: 'Turn camera on',
        cameraOff: 'Turn camera off',
        more: 'More',
        answerAudio: 'Answer without video',
        participants: 'Participants',
        inCall: '{count} in the call',
        you: 'You',
        mutedBadge: 'Muted',
        noCamera: 'Camera off',
        pin: 'Pin',
        unpin: 'Unpin',
        fit: 'Show the whole picture',
        fill: 'Fill the screen',
        selfHide: 'Hide my video',
        selfShow: 'Show my video',
        waiting: 'Waiting for them to join…',
        invited: 'ringing',
        declinedShort: 'can’t',
        screen: {
          of: '{name}’s screen',
          youPresent: 'Presenting',
          busyTitle: '{name} is presenting',
          busyHint: 'If you present, their screen stops being shared.',
          busyLockedHint: 'Only the host can present instead. Ask them to stop sharing.',
          takeOver: 'Present instead',
          start: 'Share screen',
          stop: 'Stop sharing your screen',
          startShort: 'Screen',
          stopShort: 'Stop sharing',
          sharing: 'You are sharing your screen',
          badge: 'Screen',
        },
        debug: {
          user: 'User',
          device: 'Device',
          browser: 'Browser',
          call: 'Call',
          engine: 'Call engine',
          mic: 'Mic',
          video: 'Video',
          remoteTracks: 'Their tracks',
          connectionState: 'connectionState',
        },
        panel: {
          debugTab: 'Diagnostics',
          debugMe: 'This device',
          debugAlone: 'Nobody else is connected to the call yet.',
          debugCopy: 'Copy diagnostics',
          settings: 'Settings',
          back: 'Back to the call',
          devicesTab: 'Audio and video',
          waiting: 'Waiting to join',
          pinned: 'Pinned',
          link: 'Meeting link',
          micMuted: 'Muted: nobody can hear you',
          heard: 'We can hear you',
          speakToTest: 'Speak to test it',
          cameraIsOff: 'Off: the others see your initials',
          recording: { title: 'Recording', on: 'Recording now', paused: 'Paused: not recording right now', off: 'Not recorded', cancelled: 'Cancelled: the recording was deleted', pause: 'Pause', resume: 'Resume', cancel: 'Cancel and delete', cancelSure: 'Delete what was recorded?', onlyOrganizer: 'Only the organizer can pause or cancel it.' },
          blur: 'Blur background',
          blurOn: 'Only you are sharp; the background is blurred',
          blurOff: 'What is behind you can be seen',
          blurLoading: 'Getting the blur ready…',
          nothingToChoose: 'This device has one camera, one microphone and one output: nothing to choose.',
          muted: 'Muted',
          speaking: 'Speaking',
          cameraOnShort: 'camera on',
          cameraOffShort: 'camera off',
        },
        devices: {
          title: 'Camera, mic and sound',
          camera: 'Camera',
          mic: 'Microphone',
          output: 'Play through',
          front: 'Front',
          back: 'Back',
          numbered: '{kind} {n}',
        },
        connection: {
          good: 'Good connection',
          fair: 'Fair connection',
          poor: 'Weak connection',
          reconnecting: 'Reconnecting…',
          offline: 'Offline',
        },
        notices: {
          reconnect_stuck: 'The connection dropped several times in a row. When it is working again, press Retry.',
          retry: 'Retry',
          you_are_host: 'You are now the host of the meeting: you can admit, remove and end it for everyone.',
          screen_taken: 'Someone else started presenting, so you stopped sharing.',
          mic_switched: 'The microphone was unplugged: another one is being used now.',
          video_paused: 'Your video is paused: the network cannot carry it, so voice comes first.',
          resume: 'Resume video',
          poor: 'Weak connection: video quality drops so you can still hear each other.',
          offline: 'Offline. The call picks up again on its own when the network is back.',
          too_many_for_video: 'Too many of you for video: the call carries on as audio.',
          speakingMuted: 'You are muted',
          remotePaused: '{name} is not sending video right now',
        },
        speaker: 'Output',
        minimise: 'Minimise',
        backToCall: 'Back to the call',
        rejoin: 'Back to the call',
        incomingAudio: 'Is calling you',
        incomingVideo: 'Incoming video call',
        iosHint: 'If the other person uses an iPhone, it may take a while to ring, or ring only once.',
        recording: 'This call is being recorded',
        recordingShort: 'Recording',
        pausedShort: 'Paused',
        recordingUnsupported: 'this browser cannot record your side',
        recordingLimit: 'the recording limit has been reached',
        uploading: 'uploading {count} pieces',
        transcribing: 'Transcribing the call…',
        whatWasSaid: 'What was said ({count} turns)',
        voicemailTitle: 'No answer. Leave {name} a message.',
        voicemailSent: 'Message sent.',
        phase: {
          choose: 'You are already in from another device',
          replaced: 'The call continues on another device',
          removed: 'You were removed from the call',
          outgoing: 'Calling…',
          incoming: 'Is calling you',
          connecting: 'Connecting…',
          connected: 'In a call',
          reconnecting: 'Reconnecting…',
          rejoin: 'You were in this call',
          ended: 'Call ended',
          missed: 'No answer',
          declined: 'Call declined',
          busy: 'On another call',
          failed: 'The call could not be made',
        },
        peer: {
          connecting: 'connecting',
          reconnecting: 'reconnecting',
          failed: 'no connection',
          connected: 'connected',
          new: 'connecting',
        },
        outcome: {
          answered: 'Call · {duration}',
          missed: 'Missed call',
          declined: 'Declined call',
          busy: 'Busy',
          voicemail: 'Missed call · left a voice note',
          failed: 'The call could not connect',
        },
        degraded: {
          too_many_for_video: 'Too many people for video on a mobile network: the call is audio.',
        },
        errors: {
          reconnect_stuck: 'Could not get back in: the connection keeps dropping. Press Retry when it is better.',
          meeting_over: 'This meeting is already over.',
          meeting_not_started: 'The meeting has not started yet.',
          guest_failed: 'That person could not be admitted or rejected.',
          recording_control_failed: 'The recording could not be changed.',
          recording_permission: 'The organiser controls the recording.',
          camera_lost: 'The camera was disconnected.',
          mic_lost: 'The microphone was disconnected and there is no other one.',
          removed_from_call: 'You were removed from this call.',
          host_permission: 'Only the organiser can do that.',
          remove_failed: 'That person could not be removed.',
          end_failed: 'The call could not be ended.',
          replaced: 'The call continues on another device.',
          insecure: 'Calls, the microphone and the camera need HTTPS. Use the ERP\'s secure address.',
          unsupported: 'This browser cannot make calls.',
          mic_denied: 'You have not allowed the microphone. Enable it in the browser settings.',
          mic_missing: 'No microphone was found.',
          mic_busy: 'Another application is using the microphone.',
          mic_insecure: 'The microphone needs HTTPS.',
          mic_unsupported: 'This browser cannot use the microphone.',
          mic_unknown: 'The microphone could not be used.',
          camera_denied: 'No permission for the camera: the call goes on with audio only.',
          camera_missing: 'No camera: the call goes on with audio only.',
          camera_busy: 'Another application is using the camera: the call goes on with audio only.',
          camera_insecure: 'The camera needs HTTPS.',
          camera_unsupported: 'This browser cannot use the camera.',
          camera_unknown: 'The camera could not be used: the call goes on with audio only.',
          camera_switch: 'The camera could not be switched.',
          screen_failed: 'The screen could not be shared.',
          webrtc_failed: 'Could not connect with the other person. Check the signal on both sides.',
          mic_switch: 'Could not switch microphone.',
          too_many_for_video: 'Too many of you for video: the call carries on as audio.',
          call_failed: 'The call could not be made.',
          call_permission: 'You are not allowed to make calls.',
          nobody_to_call: 'There is nobody else in this conversation.',
          too_many_for_audio: 'Too many people for one call.',
          call_ended: 'The call has already ended.',
          call_full: 'The call is full.',
          not_in_call: 'You are not in this call.',
          device_limit_reached: 'You are already in this call from three devices, which is the maximum. Leave from one of them to join from this one.',
          plan_limit_reached: 'Nobody else can join this call: the calling service plan has reached its maximum number of people.',
          call_engine_unavailable: 'Calls are not available right now: the calling service is not responding. Try again in a few minutes; messages keep working.',
          calls_not_configured: 'Calls are not switched on for this installation: the calling service (IACL Comms) has not been set up. Messages work as usual.',
        },
      },
      recording: {
        listen: 'Listen to the recording',
        none: 'No recording.',
        purged: 'The recording has already been deleted (it is kept a few days). What was said is still here in writing.',
        pieces: '{count} pieces',
      },
      callPage: {
        opening: 'Opening the call…',
        gone: 'This call has already ended',
        goneHint: 'If it was for you, it shows as missed in the conversation.',
      },
      subject: {
        opening: 'Opening the conversation…',
        notFound: 'There is no conversation here for you',
        notFoundHint: 'The record does not exist or you have no access to it.',
      },
      review: {
        title: 'Review',
        subtitle: 'What the assistant proposed and nobody has answered yet',
        emptyTitle: 'Nothing pending',
        emptyHint: 'Proposals are answered in their conversation. Only the ones waiting a long time come here.',
        failed: 'The review could not be loaded',
      },
      info: {
        rename: 'Rename',
        archive: 'Archive',
        notifications: 'My notifications',
        mute: 'Mute this conversation',
        notifyLevel: 'Notify me of',
        levels: { all: 'Everything', mentions: 'Only when named', none: 'Nothing' },
        copilot: 'Assistant',
        copilotHelp: 'The assistant reads what is said here —messages, voice notes, photos and calls— and proposes recording it in the ERP. It never writes anything without somebody accepting it.',
        copilotSwitch: 'The assistant listens to this conversation',
        copilotOffWarning: 'Everybody in the conversation will see you switched it off. While it is off nothing is transcribed or proposed.',
        copilotOffConfirm: 'Switch it off',
        people: 'People ({count})',
        you: 'you',
        remove: 'Remove {name}',
        addPlaceholder: 'Add somebody: type their name',
        about: 'Conversation about: {type}',
        kinds: { direct: 'Private conversation', group: 'Group', subject: 'Conversation of a record' },
        roles: { owner: 'Creator', admin: 'Manages', member: 'Member' },
        leave: { direct: 'Remove from my list', group: 'Leave the group', subject: 'Stop following' },
        leaveWarning: {
          direct: 'It disappears from your list until one of you writes again.',
          group: 'You will stop seeing this group and its history.',
          subject: 'You will stop getting its notifications. You can come back from the record\'s file.',
        },
      },
      push: {
        install: {
          title: 'Install Radio on your home screen',
          body: 'On iPhone, message and call notifications only arrive with the app installed: Share → Add to Home Screen, and open it from the icon.',
        },
        enable: {
          title: 'Turn on notifications',
          body: 'To hear about messages and calls with the app closed.',
          button: 'Turn on',
        },
        denied: {
          title: 'Notifications are blocked',
          body: 'Without notifications you will not hear about calls with the app closed. Unblock them in the browser settings for this site.',
        },
        iosLimits: 'On iPhone a call arrives as one notification with one sound: it does not vibrate or insist.',
        enabled: 'Notifications on',
        failed: 'Notifications could not be turned on.',
      },
      system: {
        group_created: '{by} created the group «{title}»',
        group_renamed: '{by} renamed it «{title}»',
        member_added: '{by} added {name}',
        member_removed: '{by} removed {name}',
        member_left: '{name} left the group',
        ai_on: '{by} switched the assistant on in this conversation',
        ai_off: '{by} switched the assistant off in this conversation',
        recording_on: '{by} started a call that is being recorded. Each person records their own microphone.',
        recording_paused: '{by} paused the recording.',
        recording_resumed: '{by} resumed the recording.',
        recording_cancelled: '{by} cancelled the recording: what had been recorded in this call was deleted.',
      },
      errors: {
        self_conversation: 'You cannot open a conversation with yourself.',
        groups_permission: 'You are not allowed to create groups.',
        oversee_read_only: 'Oversight reads, it does not write.',
        cannot_write: 'You cannot write in this conversation.',
        use_permission: 'You are not allowed to use Radio.',
        without_radio: 'That person does not have Radio. Ask whoever runs the ERP to give their role access.',
        manage_permission: 'You cannot change this group.',
        remove_owner: 'The person who created the group cannot be removed.',
        not_member: 'You are not in this conversation.',
        not_author: 'Only whoever wrote it can do that.',
        not_editable: 'This message cannot be edited.',
        edit_window: 'It is too late to edit it.',
        copilot_off: 'The assistant is off on this instance.',
        copilot_thread: 'The assistant is off in this conversation.',
        copilot_budget: 'This month\'s AI budget has run out.',
        effect_unknown: 'It may already have happened: check before repeating it.',
        tool_gone: 'The module that did this is no longer installed.',
        tool_forbidden: 'You are not allowed to do this.',
        call_permission: 'You are not allowed to make calls.',
        meetings_permission: 'You are not allowed to schedule meetings.',
        nobody_to_call: 'There is nobody else in this conversation.',
        too_many_for_audio: 'Too many people for one call.',
        call_ended: 'The call has already ended.',
        call_full: 'The call is full.',
        not_in_call: 'You are not in this call.',
        device_limit_reached: 'You are already in this call from three devices, which is the maximum. Leave from one of them to join from this one.',
        plan_limit_reached: 'Nobody else can join this call: the calling service plan has reached its maximum number of people.',
        call_engine_unavailable: 'Calls are not available right now: the calling service is not responding. Try again in a few minutes; messages keep working.',
        calls_not_configured: 'Calls are not switched on for this installation: the calling service (IACL Comms) has not been set up. Messages work as usual.',
        not_recording: 'This call is not being recorded.',
        recording_limit: 'The recording limit has been reached.',
      },
      related: {
        tab: 'What was said here',
        open: 'Talk here',
        openHint: 'Opens this record\'s conversation in Radio',
      },
      dashboard: {
        pending: 'Assistant proposals waiting',
        pendingHelp: 'What the assistant took from your conversations and is waiting for somebody to record or dismiss.',
        unread: 'Unread messages in Radio',
        unreadHelp: 'Messages in your conversations you have not seen yet.',
      },
      notifications: {
        knock: { title: '{name} wants to join', message: '{title}' },
        categories: {
          meetingKnock: 'Radio: someone wants to join a meeting',
          message: 'Radio: new messages',
          call: 'Radio: incoming calls',
          missedCall: 'Radio: missed calls',
          proposal: 'Radio: assistant proposals',
          budget: 'Radio: AI budget used up',
        },
        ring: {
          title: '{name} is calling you',
          titleIn: '{name} is calling you · {where}',
          voice: 'Voice call',
          video: 'Video call',
          answer: 'Answer',
          decline: 'Decline',
        },
        message: {
          voice: 'Voice note',
          photo: 'Photo',
          photoWithText: 'Photo: {text}',
          video: 'Video',
          videoWithText: 'Video: {text}',
          location: 'Location',
          file: 'File: {name}',
        },
        missed: { title: 'Missed call from {name}', message: 'They called you on Radio.' },
        proposal: { title: 'The assistant has something to record', message: '{where}' },
        budget: {
          title: 'The Radio assistant has used this month\'s budget',
          message: 'Messages and calls keep working. Nothing is transcribed or proposed until next month, or until the limit is raised (AC.10, {limit} €).',
        },
      },
    },
    assistant: {
      openers: {
        comms: { 'what-said': 'What was said about the job today?' },
      },
    },
    modules: { comms: 'Radio' },
    permissions: {
      comms: {
        use: 'Talk on Radio: messages, voice notes and the conversations of records',
        call: 'Make calls and video calls on Radio',
        meetings: 'Schedule meetings: create, change and cancel them (joining one does not need it)',
        groups: 'Create groups and manage who is in them',
        external: 'Add somebody from outside (customer portal) to a conversation',
        oversee: 'Read group and record conversations without being in them (recorded; never private ones)',
      },
    },
    settings: {
      ns: {
        comms: {
          title: 'Radio',
          subtitle: 'The company channel, its calls and its assistant',
          groups: { copilot: 'Assistant', recording: 'Call recording', calls: 'Calls', retention: 'Retention', limits: 'Limits' },
          copilot: { label: 'Assistant on', help: 'Off, the channel still works entirely —messages, voice notes and calls— but nothing is transcribed, described or proposed.' },
          extractAfterMessages: { label: 'Messages that trigger a read', help: 'The assistant reads a conversation when it goes quiet, or sooner after this many messages.' },
          extractAfterSilenceSeconds: { label: 'Silence before reading', help: 'Seconds without messages before the assistant reads what was said.' },
          recordCalls: { label: 'Record calls', help: 'Each participant records their own microphone. Everybody sees it is being recorded for the whole call, and the conversation says so. Inform your staff in writing first.' },
          recordingRetentionDays: { label: 'Days recordings are kept', help: 'After this the audio is deleted. What was said stays as text for as long as messages are kept.' },
          messageRetentionMonths: { label: 'Months messages are kept', help: 'Older messages, photos, locations and transcripts are deleted every night.' },
          ringSeconds: { label: 'Ring time', help: 'Seconds a call rings before it counts as missed and the caller is offered a voice note.' },
          maxVideoParticipants: { label: 'People in a video call', help: 'Above this a video call is offered as audio. Five or six only on good connections: each device uploads one video per person and the quality drops to fit.' },
          maxAudioParticipants: { label: 'People in an audio call', help: 'The most people in one audio call.' },
          monthlyAiBudgetEur: { label: 'Monthly AI budget', help: 'When the assistant has spent this in a month it stops until the next one, and administrators are told. The channel keeps working. Empty means no limit of its own; 0 means it spends nothing.' },
          describePhotos: { label: 'Describe photos', help: 'The assistant looks at each photo so a photo on its own can lead to a proposal.' },
          groupMaxMembers: { label: 'People in a group', help: 'Above a hundred it is a mailing list, not a conversation.' },
          reviewAfterHours: { label: 'Hours before review', help: 'A proposal unanswered this long also appears in Review.' },
          proposalExpiryDays: { label: 'Days a proposal stays open', help: 'After this an unanswered proposal expires: what was said is probably no longer news.' },
          voiceNoteMaxSeconds: { label: 'Longest voice note', help: 'Longer than five minutes nobody listens to it.' },
          recordingMaxMinutes: { label: 'Longest recording', help: 'Recording stops here and the call says so. The call itself goes on.' },
        },
      },
    },
    audit: {
      actions: {
        comms: {
          meeting_created: 'Meeting created',
          meeting_link_regenerated: 'Meeting link regenerated',
          meeting_link_revoked: 'Meeting link disabled',
          meeting_cancelled: 'Meeting cancelled',
          meeting_ended: 'Meeting ended',
          meeting_started: 'Meeting started',
          meeting_recording_enabled: 'Meeting recording enabled',
          meeting_guest_requested: 'External guest asked to join a meeting',
          meeting_guest_admitted: 'External guest admitted to a meeting',
          meeting_guest_device_added: 'External guest added another device to a meeting',
          meeting_guest_rejected: 'External guest rejected from a meeting',
          meeting_guest_removed: 'External guest removed from a meeting',
          conversation_overseen: 'Conversation read as oversight',
          copilot_on: 'Assistant switched on in a conversation',
          copilot_off: 'Assistant switched off in a conversation',
          proposal_accepted: 'Assistant proposal accepted',
          proposal_edited: 'Assistant proposal corrected before confirming',
          proposal_rejected: 'Assistant proposal dismissed',
          proposal_failed: 'Assistant proposal that could not be done',
          group_created: 'Group created',
          group_renamed: 'Group renamed',
          members_added: 'People added to a group',
          member_removed: 'Person removed from a group',
          conversation_archived: 'Conversation archived',
          conversation_unarchived: 'Conversation restored from the archive',
        },
      },
    },
  },

  fr: {
    comms: {
      meet: {
        hostLeft: 'L’organisateur est parti. La réunion continue : vous pouvez continuer à parler avec les autres.',
        another: {
          title: 'Continuer sur un autre appareil',
          hint: 'Scannez le code avec votre mobile ou votre tablette : vous entrez en votre nom, sans redemander l’accès, micro et haut-parleur coupés.',
          make: 'Afficher le code',
          copy: 'Copier le lien',
          expires: 'Il fonctionne une seule fois et expire dans 10 minutes.',
        },
        replaced: { title: 'La réunion continue dans un autre onglet.', hint: 'Vous avez ouvert le lien dans un autre onglet ou une autre fenêtre, et la réunion s’y poursuit.', again: 'Continuer ici' },
        pageTitle: 'Réunion',
        brandFallback: 'Réunion',
        kicker: 'Réunion vidéo',
        organizedBy: 'Organisée par {name}',
        calendar: { add: 'Ajouter à mon agenda :', google: 'Google', outlook: 'Outlook', ics: 'Fichier pour iPhone ou Android (.ics)', details: 'Réunion vidéo avec {who}. Rejoignez-la depuis ce lien, sans rien installer : {url}' },
        gone: {
          title: 'Cette réunion est terminée.',
          hint: 'Le lien n’est plus actif. Si vous pensez que c’est une erreur, demandez-en un nouveau à la personne qui vous a invité.',
        },
        removed: { title: 'Vous ne faites plus partie de cette réunion.', hint: 'L’organisateur vous a retiré de la salle.' },
        ended: { title: 'La réunion est terminée.', hint: 'L’organisateur l’a fermée pour tous. Vous pouvez fermer cette page.' },
      left: { title: 'Vous avez quitté la réunion.', hint: 'Si c’était par erreur, revenez. Sinon, vous pouvez fermer cette page.', again: 'Revenir dans la réunion' },
        cameraOff: 'Caméra coupée',
        noPreview: 'Testez votre caméra et votre micro avant d’entrer',
        micTest: 'Micro',
        tryDevices: 'Tester caméra et micro',
        name: 'Votre nom',
        namePlaceholder: 'Prénom et nom',
        email: 'E-mail',
        optional: '(facultatif)',
        recordingTitle: 'Cette réunion est enregistrée.',
        recordingNotice: 'Cette réunion peut être enregistrée. L’organisateur décide quand, et vous le verrez à l’écran pendant l’enregistrement.',
        recordingConsent: 'J’accepte que ma voix soit enregistrée pendant la réunion.',
        aiConsent: 'J’accepte que mes propos soient transcrits et résumés par un assistant.',
        requestAccess: 'Demander à entrer',
        entry: { title: 'Comment voulez-vous entrer ?', micOn: 'Avec micro', micOff: 'Micro coupé', cameraOn: 'Avec caméra', cameraOff: 'Sans caméra' },
        requestHint: 'L’organisateur verra votre nom et vous fera entrer.',
        waiting: { title: 'En attente d’admission…', hint: 'On sait que vous êtes là. Gardez cette page ouverte.', cancel: 'Annuler la demande' },
        rejected: { title: 'Vous n’avez pas été admis', hint: 'Vous pourrez redemander dans un instant.', again: 'Redemander à entrer' },
        admitted: { title: 'Vous êtes admis', hint: 'La salle s’ouvre dès que l’organisateur arrive.' },
        room: { alone: 'En attente des autres…' },
        chat: { attach: 'Joindre une photo, une vidéo ou un document', location: 'Envoyer ma position', title: 'Chat', empty: 'Les messages et fichiers de cette réunion apparaissent ici.' },
        errors: {
          device_link_gone: 'Ce lien a déjà été utilisé ou a expiré. Demandez-en un nouveau depuis l’autre appareil.',
          device_link_failed: 'Impossible de préparer le lien. Réessayez.',
          request_failed: 'L’envoi a échoué. Réessayez.',
          recording_consent: 'Cette réunion est enregistrée : vous devez l’accepter pour entrer.',
          removed: 'Vous avez été retiré de cette réunion et ne pouvez plus y entrer.',
          too_many: 'Vous avez demandé à entrer trop de fois.',
          wait: 'Attendez quelques secondes avant de redemander.',
          door_busy: 'Beaucoup de personnes attendent d’entrer. Réessayez dans un instant.',
          gone: 'Cette réunion est terminée.',
          meeting_over: 'Cette réunion est terminée.',
          meeting_not_started: 'La salle n’est pas encore ouverte.',
          call_failed: 'Impossible d’entrer dans la salle. Réessayez.',
        },
      },
      meetings: {
        admitAll: 'Tout admettre',
        waitingIn: '{count} en attente dans « {title} »',
        title: 'Réunions',
        subtitle: 'Appels vidéo avec le bureau, le terrain et des personnes extérieures',
        provider: 'Radio (appel vidéo)',
        kicker: 'Réunion',
        new: 'Nouvelle réunion',
        edit: 'Modifier la réunion',
        create: 'Créer et copier le lien',
        failed: 'Impossible de charger vos réunions',
        emptyTitle: 'Vous n’avez aucune réunion',
        emptyHint: 'Créez-en une et partagez le lien : les personnes sans compte entrent depuis leur navigateur, sans rien installer.',
        notFound: 'Cette réunion n’existe pas ou vous ne pouvez pas la voir',
        groups: { live: 'Maintenant', next: 'À venir', past: 'Précédentes' },
        status: { scheduled: 'Programmée', live: 'En cours', ended: 'Terminée', cancelled: 'Annulée' },
        guestsCount: '{count} invité externe | {count} invités externes',
        waitingCount: '{count} en attente',
        recorded: 'Enregistrée',
        aiOn: 'Assistant',
        organizer: 'Organisée par {name}',
        lasted: 'A duré {minutes} min',
        join: 'Entrer avec la vidéo',
        joinLive: 'Entrer dans la salle',
        joinAudio: 'Entrer en audio seul',
        enter: 'Entrer dans la réunion',
        backToRoom: 'Revenir à la salle',
        inOtherCall: 'Vous êtes dans un autre appel. Raccrochez pour entrer.',
        wasCancelled: 'Cette réunion a été annulée.',
        wasEnded: 'Cette réunion est terminée.',
        openChat: 'Ouvrir la conversation de la réunion',
        openMeeting: 'Voir la réunion',
        openInCalendar: 'Voir dans le calendrier',
        endForAll: 'Terminer pour tous',
        endConfirm: 'L’appel se termine pour tous, les invités sortent et le lien cesse de fonctionner.',
        cancel: 'Annuler la réunion',
        cancelConfirm: 'Personne ne pourra entrer et le lien cessera de fonctionner.',
        link: 'Lien pour les invités',
        copyLink: 'Copier le lien',
        copied: 'Copié',
        copyFailed: 'Impossible de copier. Sélectionnez le lien et copiez-le à la main.',
        linkHint: 'Qui l’ouvre demande à entrer et vous décidez qui passe.',
        linkHintOpen: 'Sans salle d’attente : toute personne ayant le lien entre directement.',
        expires: 'Valable jusqu’au {when}.',
        revoked: 'Le lien est désactivé. Personne de l’extérieur ne peut entrer.',
        noLink: 'Seul l’organisateur peut partager le lien.',
        noExternal: 'Cette réunion n’accepte pas de personnes extérieures.',
        regenerate: 'Nouveau lien',
        regenerateConfirm: 'Le lien actuel cessera de fonctionner. Ceux qui l’ont auront besoin du nouveau.',
        regenerated: 'Nouveau lien prêt. L’ancien ne fonctionne plus.',
        enableLink: 'Créer le lien',
        revoke: 'Désactiver le lien',
        revokeConfirm: 'Plus personne ne pourra demander à entrer. Ceux déjà présents restent.',
        door: 'Invités externes',
        noGuests: 'Personne n’a encore frappé à la porte.',
        decidedBy: 'par {name}',
        consentRecording: 'enregistrement',
        consentAi: 'transcription',
        recording: {
          title: 'Enregistrement',
          loading: 'Recherche de l’enregistrement…',
          failed: 'Impossible de charger l’enregistrement',
          none: 'Rien n’a été enregistré dans cette réunion. L’enregistrement se fait quand il est activé dans les réglages de Radio et dans la réunion, et que quelqu’un d’autre est entré.',
          whole: 'La réunion',
          part: 'Partie {n}',
          purged: 'L’audio a été supprimé à la fin de la durée de conservation ; ce qui a été dit reste.',
          prepare: 'Préparer l’audio',
          mixing: 'Assemblage des voix… {done} sur {total}',
          mixFailed: 'Impossible de préparer l’audio',
          speed: 'Vitesse',
          download: 'Télécharger l’audio',
          transcript: 'Ce qui a été dit',
          transcriptPending: 'Transcription en cours… elle apparaîtra ici dans quelques minutes.',
          transcriptOff: 'Pas de transcription : l’IA n’est pas activée.',
        },
        admit: 'Admettre',
        reject: 'Refuser',
        admitAnyway: 'Admettre',
        remove: 'Retirer',
        removeSure: 'Retirer ?',
        external: 'Externe',
        waitingBadge: 'En attente',
        guestStates: {
          requested: 'En attente',
          admitted: 'Admis',
          connected: 'Connecté',
          rejected: 'Refusé',
          left: 'Parti',
          removed: 'Expulsé',
          withdrawn: 'Parti avant d’entrer',
        },
        afterTitle: 'Ce qui est ressorti de la réunion',
        summaryPending: 'L’assistant prépare le résumé. Il apparaîtra ici et dans la conversation.',
        knocks: 'Personnes qui attendent d’entrer',
        knock: '{name} veut entrer',
        again: 'demande pour la {count}e fois',
        moreKnocks: '{count} autres en attente',
        fields: {
          title: 'Titre',
          titlePlaceholder: 'Point chantier avec le fournisseur',
          day: 'Jour',
          start: 'Début',
          end: 'Fin',
          internal: 'De l’entreprise',
          internalHint: 'Ils entrent directement, sans attendre.',
          searchPeople: 'Chercher quelqu’un…',
          guests: 'De l’extérieur',
          guestsHint: 'Facultatif : pour les reconnaître quand ils frappent. Le lien se partage de toute façon.',
          guestName: 'Nom',
          guestEmail: 'E-mail',
          addGuest: 'Ajouter un invité',
          externalAccess: 'Des personnes extérieures peuvent entrer avec le lien',
          waitingRoom: 'Salle d’attente : je décide qui entre',
          ai: 'Assistant : résumé et propositions à la fin',
          recording: 'Enregistrer la réunion',
          recordingHint: 'Tout le monde est prévenu pendant toute la réunion et les invités externes doivent l’accepter pour entrer. N’enregistre que si l’enregistrement est activé dans les réglages de Radio.',
          noWaitingRoomHint: 'Sans salle d’attente, toute personne ayant le lien entre sans demander.',
          inCalendar: 'Enregistrée dans le calendrier : elle arrive dans l’agenda de chaque personne de l’entreprise invitée, avec son invitation.',
        },
        errors: {
          title: 'Donnez-lui un titre.',
          when: 'Vérifiez le jour et les heures.',
          tooLong: 'Une réunion ne peut pas durer plus de 12 heures.',
          save: 'Impossible d’enregistrer. Réessayez.',
          decide: 'Cela n’a pas fonctionné. Réessayez.',
        },
      },
      title: 'Radio',
      back: 'Retour au canal',
      retry: 'Réessayer',
      help: {
        title: 'Radio',
        subtitle: 'Parler avec le bureau et le terrain, appeler, envoyer des notes vocales et laisser l’assistant noter ce qui se dit',
      },
      connection: {
        idle: 'Non connecté',
        connecting: 'Connexion…',
        open: 'Connecté',
        retrying: 'Reconnexion…',
        offline: 'Hors ligne',
      },
      list: {
        emptyTitle: 'Vous ne parlez encore avec personne',
        emptyHint: 'Touchez « Nouvelle » et choisissez une personne. La fiche d’un chantier ou d’un site ouvre aussi sa conversation.',
        failed: 'Impossible de charger vos conversations',
        failedHint: 'Vérifiez la connexion et réessayez.',
        noMessages: 'Pas encore de messages',
        you: 'Vous',
        inCall: 'Appel en cours',
        showArchived: 'Voir les archivées',
        hideArchived: 'Masquer les archivées',
        noArchived: 'Rien d’archivé.',
        restore: 'Restaurer',
      },
      pane: {
        title: 'Choisissez une conversation',
        hint: 'Ou commencez-en une nouvelle. Maintenez le micro appuyé pour envoyer une note vocale.',
      },
      kinds: {
        text: 'Message',
        voice: 'Note vocale',
        photo: 'Photo',
        video: 'Vidéo',
        file: 'Fichier',
        location: 'Position',
        system: 'Avis',
        call: 'Appel',
        proposal: 'Proposition de l’assistant',
        deleted: 'Message supprimé',
      },
      subjects: {
        property: 'Site',
        work: 'Chantier',
        project: 'Projet',
        incident: 'Incident',
        work_order: 'Ordre de travail',
        customer: 'Client',
        asset: 'Machine',
        vehicle: 'Véhicule',
        production_order: 'Ordre de fabrication',
        location: 'Entrepôt',
        team: 'Équipe',
      },
      presence: {
        online: 'En ligne',
        offline: 'Hors ligne',
        seen: 'Vu {when}',
        onlineCount: '{count} en ligne',
      },
      new: {
        title: 'Nouvelle conversation',
        short: 'Nouvelle',
        person: 'Une personne',
        group: 'Un groupe',
        search: 'Chercher par nom',
        typeName: 'Tapez un nom',
        nobody: 'Personne ne porte ce nom',
        groupName: 'Nom du groupe',
        groupPlaceholder: 'L’équipe, le bureau, les chefs d’équipe…',
        create: 'Créer le groupe',
      },
      search: {
        placeholder: 'Chercher dans vos conversations',
        inThread: 'Chercher',
        nothing: 'Aucun résultat',
        mode: {
          semantic: 'Recherche par le sens',
          text: 'Recherche par les mots',
        },
      },
      thread: {
        openRecord: 'Voir {kind}',
        tooOld: 'Ce message est trop ancien pour être chargé ici.',
        loading: 'Chargement…',
        loadFailed: 'Impossible de charger la conversation',
        emptyTitle: 'Rien ici pour l’instant',
        emptyHint: 'Écrivez quelque chose ou maintenez le micro appuyé pour parler.',
        start: 'Début de la conversation',
        today: 'Aujourd’hui',
        yesterday: 'Hier',
        typing: '{names} est en train d’écrire… | {names} sont en train d’écrire…',
        newMessages: '{count} nouveau message | {count} nouveaux messages',
        members: '{count} personne | {count} personnes',
        info: 'Détails',
        gone: 'Vous ne faites plus partie de cette conversation.',
        overseeing: 'Vous lisez cette conversation en supervision. Chaque ouverture est enregistrée.',
        overseeReadOnly: 'En supervision, on lit, on n’écrit pas.',
        cannotWrite: 'Vous ne pouvez pas écrire dans cette conversation.',
        copilotOffHere: 'L’assistant est désactivé dans cette conversation.',
        callInProgress: 'Un appel est en cours ici.',
        joinCall: 'Rejoindre',
      },
      message: {
        goToReplied: 'Aller au message cité',
        earlier: 'Message précédent',
        earlierHint: 'Touchez pour le voir',
        more: 'Voir plus',
        less: 'Voir moins',
        deleted: 'Message supprimé',
        edited: 'modifié',
        sending: 'Envoi',
        sent: 'Envoyé',
        read: 'Lu',
        readBy: 'Lu par {count}',
        failed: 'Impossible d’envoyer.',
        offline: 'Hors ligne : il partira au retour du réseau.',
        tooLarge: 'Le fichier est trop volumineux.',
        retry: 'Réessayer',
        discard: 'Abandonner',
        uploading: 'Envoi du fichier',
        reply: 'Répondre',
        copy: 'Copier',
        edit: 'Modifier',
        delete: 'Supprimer',
        deleteTitle: 'Supprimer ce message ?',
        deleteHelp: 'Il restera comme « message supprimé » pour tout le monde. Le fichier, la note ou la photo sont effacés.',
        openPhoto: 'Voir la photo',
        seenHelp: 'Ce que l’assistant voit sur la photo',
      },
      composer: {
        tray: {
          camera: 'Caméra',
          gallery: 'Galerie',
          video: 'Vidéo',
          document: 'Document',
          location: 'Position',
        },
        attach: 'Joindre',
        placeholderIn: 'Écrire dans {where}',
        replyingTo: 'Réponse à {name}',
        placeholder: 'Écrivez un message',
        send: 'Envoyer',
        camera: 'Prendre une photo',
        gallery: 'Photo ou vidéo de la galerie',
        video: 'Filmer une vidéo',
        document: 'Un document',
        location: 'Ma position',
        tooLarge: '« {name} » dépasse {mb} Mo et ne peut pas être envoyé.',
      },
      voice: {
        speed: 'Vitesse {rate}',
        hold: 'Maintenez appuyé pour parler',
        release: 'Relâchez pour envoyer',
        holdHint: 'Maintenez le micro appuyé pendant que vous parlez, puis relâchez pour envoyer.',
        slideToCancel: 'Glissez pour annuler',
        releaseToCancel: 'Relâchez pour annuler',
        left: 'Reste {seconds} s',
        play: 'Lire',
        pause: 'Pause',
        cannotPlay: 'Ce navigateur ne peut pas lire la note. Téléchargez-la pour l’écouter.',
      },
      transcript: {
        pending: 'Transcription…',
        failed: 'Transcription impossible.',
        retry: 'Réessayer',
        disabled: {
          provider: 'Cette instance n’a pas de transcription : la note s’écoute, mais sans texte.',
          budget: 'Pas de transcription : le budget IA du mois est épuisé.',
          off: 'Pas de transcription : l’assistant est désactivé sur cette instance.',
          thread: 'Pas de transcription : l’assistant est désactivé dans cette conversation.',
          error: 'Transcription impossible.',
        },
      },
      location: {
        shared: 'Position partagée',
        accuracy: 'Précision d’environ {meters} m',
        denied: 'Vous n’avez pas autorisé la localisation. Activez-la dans les réglages du navigateur.',
        timeout: 'Impossible de savoir où vous êtes à temps. Essayez à l’extérieur.',
        unavailable: 'Cet appareil ne peut pas partager la position d’ici.',
      },
      copilot: {
        ask: 'Qu’en retiens-tu ?',
        found: 'L’assistant propose {count} chose | L’assistant propose {count} choses',
        nothing: 'L’assistant ne voit rien à noter.',
        offInstance: 'L’assistant est désactivé sur cette instance.',
        budget: 'Le budget IA du mois est épuisé : l’assistant ne transcrit ni ne propose rien jusqu’au mois prochain. Les messages et les appels fonctionnent normalement.',
        noTranscription: 'Cette instance n’a pas de transcription : l’assistant ne lit que l’écrit.',
      },
      proposal: {
        summaryHead: 'Résumé de la réunion',
        summaryTitle: 'Résumé',
        summaryNoActions: 'Rien à consigner dans l’ERP.',
        title: 'Action détectée',
        heard: 'A compris',
        willDo: 'Action',
        more: 'Voir plus',
        less: 'Voir moins',
        accept: 'Confirmer',
        reject: 'Écarter',
        retry: 'Réessayer',
        executing: 'Enregistrement…',
        done: 'Noté dans l’ERP',
        failed: 'Impossible : {reason}',
        effectUnknown: 'On ne sait pas si c’est passé (trop long). Vérifiez avant de recommencer.',
        titleMany: '{count} actions détectées',
        where: 'Où',
        willChange: 'Ce qui va changer',
        changed: 'Ce qui a changé',
        open: 'Ouvrir',
        doneTitle: 'Action réalisée',
        doneBy: 'Confirmée par {name} · {when}',
        rejectedTitle: 'Proposition écartée',
        expiredTitle: 'Proposition expirée',
        failedTitle: 'L’action n’a pas pu être réalisée.',
        failedWhy: {
          invalid: 'Le module n’a pas accepté les données : corrigez-les et réessayez.',
          gone: 'Ce qu’il fallait modifier n’existe plus.',
          conflict: 'Quelque chose a changé entre-temps : vérifiez et réessayez.',
          timeout: 'La réponse a pris trop de temps.',
          error: 'Réessayez dans un instant.',
        },
        edit: 'Corriger',
        saveEdit: 'Enregistrer',
        editInvalid: 'Cette valeur n’est pas valable pour cette action.',
        editedBy: 'Corrigée par {name} avant confirmation',
        echo: {
          pending: 'Attention : la même chose a été proposée le {when} et attend toujours une réponse. N’en enregistrez qu’une si c’est la même.',
          executing: 'Attention : la même chose est en cours d’enregistrement (proposée le {when}).',
          accepted: 'Attention : la même chose a déjà été enregistrée (proposée le {when}). Ne l’enregistrez que si c’est vraiment une nouvelle fois.',
          rejected: 'La même chose a été proposée le {when} et écartée.',
        },
        noPermission: 'Vous n’avez pas le droit de faire ceci : une autre personne de la conversation peut le noter.',
        trigger: {
          silence: 'De la conversation',
          count: 'De la conversation',
          call: 'De l’appel',
          manual: 'À votre demande',
        },
        state: {
          accepted: 'Noté par {name}, {when}',
          rejected: 'Écarté par {name}',
          expired: 'Expiré sans réponse',
          pending: 'En attente',
        },
      },
      call: {
        makeHost: 'Nommer organisateur',
        host: 'Organisateur',
        youTile: '{name} (vous)',
        thisDevice: 'cet appareil',
        removedShort: 'Retiré de l’appel',
        speakerButton: 'Haut-parleur',
        speakerOn: 'Activer le haut-parleur',
        speakerOff: 'Couper le haut-parleur',
        device: { desktop: 'Ordinateur', tablet: 'Tablette', phone: 'Mobile', unknown: 'Appareil', companion: '{device} (appareil secondaire)' },
        companion: {
          title: 'Vous êtes déjà dans cette réunion depuis un autre appareil',
          hint: 'Choisissez comment rejoindre depuis ici.',
          fromDevice: 'Vous y êtes actuellement depuis : {device}.',
          join: 'Rejoindre comme appareil secondaire',
          joinHint: 'Pour voir l’écran ou montrer quelque chose. Vous entrez micro, haut-parleur et caméra coupés.',
          replace: 'Passer sur cet appareil',
          replaceHint: 'Vous continuez la réunion ici et l’autre appareil la quitte.',
          banner: 'Mode appareil secondaire : audio et micro coupés pour éviter l’écho.',
          bannerOn: 'Mode appareil secondaire : si l’autre appareil est proche, il peut y avoir de l’écho.',
        },
        leave: {
          title: 'Quitter, ou mettre fin pour tous ?',
          justMe: 'Partir seul',
          justMeHint: 'Les autres continuent de parler.',
          everyone: 'Mettre fin pour tous',
          everyoneHint: 'L’appel est raccroché pour tout le monde.',
          everyoneSure: 'Appuyez encore pour mettre fin pour tous',
        },
        audio: 'Appeler',
        video: 'Appel vidéo',
        answer: 'Répondre',
        decline: 'Refuser',
        hangup: 'Raccrocher',
        mute: 'Couper le micro',
        unmute: 'Activer le micro',
        camera: 'Caméra',
        flip: 'Changer de caméra',
        flipShort: 'Retourner',
        blurShort: 'Flouter',
        cameraOn: 'Activer la caméra',
        cameraOff: 'Couper la caméra',
        more: 'Plus',
        answerAudio: 'Répondre sans vidéo',
        participants: 'Participants',
        inCall: '{count} dans l’appel',
        you: 'Vous',
        mutedBadge: 'Micro coupé',
        noCamera: 'Caméra coupée',
        pin: 'Épingler',
        unpin: 'Désépingler',
        fit: 'Voir l’image entière',
        fill: 'Remplir l’écran',
        selfHide: 'Masquer ma vidéo',
        selfShow: 'Afficher ma vidéo',
        waiting: 'En attente de connexion…',
        invited: 'appelé',
        declinedShort: 'indisponible',
        screen: {
          of: 'Écran de {name}',
          youPresent: 'Présentation',
          busyTitle: '{name} présente',
          busyHint: 'Si vous présentez, son écran cesse d’être partagé.',
          busyLockedHint: 'Seul l’organisateur peut présenter à sa place. Demandez-lui d’arrêter le partage.',
          takeOver: 'Présenter à sa place',
          start: 'Partager l’écran',
          stop: 'Arrêter de partager l’écran',
          startShort: 'Écran',
          stopShort: 'Arrêter le partage',
          sharing: 'Vous partagez votre écran',
          badge: 'Écran',
        },
        debug: {
          user: 'Utilisateur',
          device: 'Appareil',
          browser: 'Navigateur',
          call: 'Appel',
          engine: 'Moteur d’appels',
          mic: 'Micro',
          video: 'Vidéo',
          remoteTracks: 'Ses pistes',
          connectionState: 'connectionState',
        },
        panel: {
          debugTab: 'Diagnostic',
          debugMe: 'Cet appareil',
          debugAlone: 'Personne d’autre n’est encore connecté à l’appel.',
          debugCopy: 'Copier le diagnostic',
          settings: 'Réglages',
          back: 'Revenir à l’appel',
          devicesTab: 'Audio et vidéo',
          waiting: 'En attente d’entrer',
          pinned: 'Épinglé',
          link: 'Lien de la réunion',
          micMuted: 'Coupé : personne ne vous entend',
          heard: 'On vous entend',
          speakToTest: 'Parlez pour le tester',
          cameraIsOff: 'Coupée : les autres voient vos initiales',
          recording: { title: 'Enregistrement', on: 'Enregistrement en cours', paused: 'En pause : rien n’est enregistré', off: 'Pas d’enregistrement', cancelled: 'Annulé : l’enregistrement a été supprimé', pause: 'Pause', resume: 'Reprendre', cancel: 'Annuler et supprimer', cancelSure: 'Supprimer ce qui a été enregistré ?', onlyOrganizer: 'Seul l’organisateur peut le mettre en pause ou l’annuler.' },
          blur: 'Flouter l’arrière-plan',
          blurOn: 'Seul vous êtes net ; l’arrière-plan est flou',
          blurOff: 'On voit ce qu’il y a derrière vous',
          blurLoading: 'Préparation du flou…',
          nothingToChoose: 'Cet appareil n’a qu’une caméra, un micro et une sortie : rien à choisir.',
          muted: 'Micro coupé',
          speaking: 'Parle',
          cameraOnShort: 'caméra active',
          cameraOffShort: 'sans caméra',
        },
        devices: {
          title: 'Caméra, micro et son',
          camera: 'Caméra',
          mic: 'Micro',
          output: 'Son sur',
          front: 'Avant',
          back: 'Arrière',
          numbered: '{kind} {n}',
        },
        connection: {
          good: 'Bonne connexion',
          fair: 'Connexion moyenne',
          poor: 'Connexion faible',
          reconnecting: 'Reconnexion…',
          offline: 'Hors ligne',
        },
        notices: {
          reconnect_stuck: 'La connexion a été coupée plusieurs fois de suite. Quand elle refonctionne, appuyez sur Réessayer.',
          retry: 'Réessayer',
          you_are_host: 'Vous dirigez maintenant la réunion : vous pouvez admettre, retirer et mettre fin pour tous.',
          screen_taken: 'Quelqu’un d’autre a commencé à présenter : vous avez arrêté de partager.',
          mic_switched: 'Le micro a été débranché : un autre est utilisé.',
          video_paused: 'Votre vidéo est en pause : le réseau ne suit pas, la voix passe en premier.',
          resume: 'Reprendre la vidéo',
          poor: 'Connexion faible : la vidéo baisse en qualité pour que vous continuiez à vous entendre.',
          offline: 'Hors ligne. L’appel reprend tout seul quand le réseau revient.',
          too_many_for_video: 'Trop nombreux pour la vidéo : l’appel continue en audio.',
          speakingMuted: 'Votre micro est coupé',
          remotePaused: '{name} n’envoie pas de vidéo pour l’instant',
        },
        speaker: 'Sortie',
        minimise: 'Réduire',
        backToCall: 'Retour à l’appel',
        rejoin: 'Retour à l’appel',
        incomingAudio: 'Vous appelle',
        incomingVideo: 'Appel vidéo entrant',
        iosHint: 'Si l’autre personne utilise un iPhone, la sonnerie peut tarder ou ne sonner qu’une fois.',
        recording: 'Cet appel est enregistré',
        recordingShort: 'Enregistrement',
        pausedShort: 'En pause',
        recordingUnsupported: 'ce navigateur ne peut pas enregistrer votre partie',
        recordingLimit: 'la durée maximale d’enregistrement est atteinte',
        uploading: 'envoi de {count} morceaux',
        transcribing: 'Transcription de l’appel…',
        whatWasSaid: 'Ce qui a été dit ({count} interventions)',
        voicemailTitle: 'Pas de réponse. Laissez un message à {name}.',
        voicemailSent: 'Message envoyé.',
        phase: {
          choose: 'Vous y êtes déjà depuis un autre appareil',
          replaced: 'L’appel continue sur un autre appareil',
          removed: 'Vous avez été retiré de l’appel',
          outgoing: 'Appel…',
          incoming: 'Vous appelle',
          connecting: 'Connexion…',
          connected: 'En communication',
          reconnecting: 'Reconnexion…',
          rejoin: 'Vous étiez dans cet appel',
          ended: 'Appel terminé',
          missed: 'Pas de réponse',
          declined: 'Appel refusé',
          busy: 'Déjà en ligne',
          failed: 'Appel impossible',
        },
        peer: {
          connecting: 'connexion',
          reconnecting: 'reconnexion',
          failed: 'hors ligne',
          connected: 'connecté',
          new: 'connexion',
        },
        outcome: {
          answered: 'Appel · {duration}',
          missed: 'Appel manqué',
          declined: 'Appel refusé',
          busy: 'Occupé',
          voicemail: 'Appel manqué · a laissé une note vocale',
          failed: 'L’appel n’a pas pu se connecter',
        },
        degraded: {
          too_many_for_video: 'Vous êtes trop nombreux pour la vidéo sur un réseau mobile : l’appel est audio.',
        },
        errors: {
          reconnect_stuck: 'Impossible de revenir : la connexion se coupe sans arrêt. Appuyez sur Réessayer quand elle ira mieux.',
          meeting_over: 'Cette réunion est déjà terminée.',
          meeting_not_started: 'La réunion n’a pas encore commencé.',
          guest_failed: 'Impossible d’admettre ou de refuser cette personne.',
          recording_control_failed: 'Impossible de modifier l’enregistrement.',
          recording_permission: 'C’est l’organisateur qui contrôle l’enregistrement.',
          camera_lost: 'La caméra a été déconnectée.',
          mic_lost: 'Le micro a été déconnecté et il n’y en a pas d’autre.',
          removed_from_call: 'Vous avez été retiré de cet appel.',
          host_permission: 'Seul l’organisateur peut faire cela.',
          remove_failed: 'Impossible de retirer cette personne.',
          end_failed: 'Impossible de mettre fin à l’appel.',
          replaced: 'L’appel continue sur un autre appareil.',
          insecure: 'Les appels, le micro et la caméra exigent HTTPS. Passez par l’adresse sécurisée de l’ERP.',
          unsupported: 'Ce navigateur ne peut pas passer d’appels.',
          mic_denied: 'Vous n’avez pas autorisé le micro. Activez-le dans les réglages du navigateur.',
          mic_missing: 'Aucun micro trouvé.',
          mic_busy: 'Une autre application utilise le micro.',
          mic_insecure: 'Le micro exige HTTPS.',
          mic_unsupported: 'Ce navigateur ne peut pas utiliser le micro.',
          mic_unknown: 'Impossible d’utiliser le micro.',
          camera_denied: 'Pas d’accès à la caméra : l’appel continue en audio seul.',
          camera_missing: 'Pas de caméra : l’appel continue en audio seul.',
          camera_busy: 'Une autre application utilise la caméra : l’appel continue en audio seul.',
          camera_insecure: 'La caméra exige HTTPS.',
          camera_unsupported: 'Ce navigateur ne peut pas utiliser la caméra.',
          camera_unknown: 'Impossible d’utiliser la caméra : l’appel continue en audio seul.',
          camera_switch: 'Impossible de changer de caméra.',
          screen_failed: 'Impossible de partager l’écran.',
          webrtc_failed: 'Impossible de joindre l’autre personne. Vérifiez le réseau.',
          mic_switch: 'Impossible de changer de micro.',
          too_many_for_video: 'Trop nombreux pour la vidéo : l’appel continue en audio.',
          call_failed: 'Impossible de passer l’appel.',
          call_permission: 'Vous n’avez pas le droit d’appeler.',
          nobody_to_call: 'Il n’y a personne d’autre dans cette conversation.',
          too_many_for_audio: 'Vous êtes trop nombreux pour un appel.',
          call_ended: 'L’appel est déjà terminé.',
          call_full: 'L’appel est complet.',
          not_in_call: 'Vous n’êtes pas dans cet appel.',
          device_limit_reached: 'Vous êtes déjà dans cet appel depuis trois appareils, le maximum. Quittez-le depuis l’un d’eux pour le rejoindre depuis celui-ci.',
          plan_limit_reached: 'Personne d’autre ne peut rejoindre cet appel : le maximum de personnes prévu par le service d’appels souscrit est atteint.',
          call_engine_unavailable: 'Les appels ne sont pas disponibles pour le moment : le service d’appels ne répond pas. Réessayez dans quelques minutes ; les messages fonctionnent toujours.',
          calls_not_configured: 'Les appels ne sont pas activés sur cette installation : le service d’appels (IACL Comms) n’est pas configuré. Les messages fonctionnent normalement.',
        },
      },
      recording: {
        listen: 'Écouter l’enregistrement',
        none: 'Pas d’enregistrement.',
        purged: 'L’enregistrement a déjà été effacé (il est gardé quelques jours). Ce qui a été dit reste écrit ici.',
        pieces: '{count} morceaux',
      },
      callPage: {
        opening: 'Ouverture de l’appel…',
        gone: 'Cet appel est déjà terminé',
        goneHint: 'S’il était pour vous, il apparaîtra comme manqué dans la conversation.',
      },
      subject: {
        opening: 'Ouverture de la conversation…',
        notFound: 'Pas de conversation ici pour vous',
        notFoundHint: 'L’enregistrement n’existe pas ou vous n’y avez pas accès.',
      },
      review: {
        title: 'À revoir',
        subtitle: 'Ce que l’assistant a proposé et à quoi personne n’a encore répondu',
        emptyTitle: 'Rien en attente',
        emptyHint: 'On répond aux propositions dans leur conversation. N’arrivent ici que celles qui attendent depuis longtemps.',
        failed: 'Impossible de charger la liste à revoir',
      },
      info: {
        rename: 'Renommer',
        archive: 'Archiver',
        notifications: 'Mes notifications',
        mute: 'Mettre cette conversation en sourdine',
        notifyLevel: 'Me prévenir pour',
        levels: { all: 'Tout', mentions: 'Seulement si on me mentionne', none: 'Rien' },
        copilot: 'Assistant',
        copilotHelp: 'L’assistant lit ce qui se dit ici —messages, notes vocales, photos et appels— et propose de le noter dans l’ERP. Il n’écrit jamais rien sans que quelqu’un l’accepte.',
        copilotSwitch: 'L’assistant écoute cette conversation',
        copilotOffWarning: 'Tous les membres de la conversation verront que vous l’avez désactivé. Tant qu’il est désactivé, rien n’est transcrit ni proposé.',
        copilotOffConfirm: 'Le désactiver',
        people: 'Personnes ({count})',
        you: 'vous',
        remove: 'Retirer {name}',
        addPlaceholder: 'Ajouter quelqu’un : tapez son nom',
        about: 'Conversation sur : {type}',
        kinds: { direct: 'Conversation privée', group: 'Groupe', subject: 'Conversation d’un enregistrement' },
        roles: { owner: 'Créateur', admin: 'Administre', member: 'Membre' },
        leave: { direct: 'Retirer de ma liste', group: 'Quitter le groupe', subject: 'Ne plus suivre' },
        leaveWarning: {
          direct: 'Elle disparaît de votre liste jusqu’à ce que l’un de vous deux écrive à nouveau.',
          group: 'Vous ne verrez plus ce groupe ni son historique.',
          subject: 'Vous ne recevrez plus ses notifications. Vous pourrez revenir depuis la fiche de l’enregistrement.',
        },
      },
      push: {
        install: {
          title: 'Installez Radio sur l’écran d’accueil',
          body: 'Sur iPhone, les notifications de messages et d’appels n’arrivent qu’avec l’application installée : Partager → Sur l’écran d’accueil, puis ouvrez-la depuis l’icône.',
        },
        enable: {
          title: 'Activez les notifications',
          body: 'Pour être prévenu des messages et des appels quand l’application est fermée.',
          button: 'Activer',
        },
        denied: {
          title: 'Les notifications sont bloquées',
          body: 'Sans notifications, vous ne saurez pas qu’on vous appelle quand l’application est fermée. Débloquez-les dans les réglages du navigateur pour ce site.',
        },
        iosLimits: 'Sur iPhone, un appel arrive comme une notification avec un son, une seule fois : pas de vibration ni d’insistance.',
        enabled: 'Notifications activées',
        failed: 'Impossible d’activer les notifications.',
      },
      system: {
        group_created: '{by} a créé le groupe « {title} »',
        group_renamed: '{by} a renommé le groupe en « {title} »',
        member_added: '{by} a ajouté {name}',
        member_removed: '{by} a retiré {name}',
        member_left: '{name} a quitté le groupe',
        ai_on: '{by} a activé l’assistant dans cette conversation',
        ai_off: '{by} a désactivé l’assistant dans cette conversation',
        recording_on: '{by} a lancé un appel enregistré. Chacun enregistre son propre micro.',
        recording_paused: '{by} a mis l’enregistrement en pause.',
        recording_resumed: '{by} a repris l’enregistrement.',
        recording_cancelled: '{by} a annulé l’enregistrement : ce qui avait été enregistré dans cet appel a été supprimé.',
      },
      errors: {
        self_conversation: 'Vous ne pouvez pas ouvrir une conversation avec vous-même.',
        groups_permission: 'Vous n’avez pas le droit de créer des groupes.',
        oversee_read_only: 'En supervision, on lit, on n’écrit pas.',
        cannot_write: 'Vous ne pouvez pas écrire dans cette conversation.',
        use_permission: 'Vous n’avez pas le droit d’utiliser Radio.',
        without_radio: 'Cette personne n’a pas Radio. Demandez à qui administre l’ERP de donner l’accès à son rôle.',
        manage_permission: 'Vous ne pouvez pas modifier ce groupe.',
        remove_owner: 'Impossible de retirer la personne qui a créé le groupe.',
        not_member: 'Vous n’êtes pas dans cette conversation.',
        not_author: 'Seul l’auteur du message peut le faire.',
        not_editable: 'Ce message ne peut pas être modifié.',
        edit_window: 'Trop de temps a passé pour le modifier.',
        copilot_off: 'L’assistant est désactivé sur cette instance.',
        copilot_thread: 'L’assistant est désactivé dans cette conversation.',
        copilot_budget: 'Le budget IA du mois est épuisé.',
        effect_unknown: 'C’est peut-être déjà fait : vérifiez avant de recommencer.',
        tool_gone: 'Le module qui faisait ceci n’est plus installé.',
        tool_forbidden: 'Vous n’avez pas le droit de faire ceci.',
        call_permission: 'Vous n’avez pas le droit d’appeler.',
        meetings_permission: 'Vous n’avez pas le droit d’organiser des réunions.',
        nobody_to_call: 'Il n’y a personne d’autre dans cette conversation.',
        too_many_for_audio: 'Vous êtes trop nombreux pour un appel.',
        call_ended: 'L’appel est déjà terminé.',
        call_full: 'L’appel est complet.',
        not_in_call: 'Vous n’êtes pas dans cet appel.',
        device_limit_reached: 'Vous êtes déjà dans cet appel depuis trois appareils, le maximum. Quittez-le depuis l’un d’eux pour le rejoindre depuis celui-ci.',
        plan_limit_reached: 'Personne d’autre ne peut rejoindre cet appel : le maximum de personnes prévu par le service d’appels souscrit est atteint.',
        call_engine_unavailable: 'Les appels ne sont pas disponibles pour le moment : le service d’appels ne répond pas. Réessayez dans quelques minutes ; les messages fonctionnent toujours.',
        calls_not_configured: 'Les appels ne sont pas activés sur cette installation : le service d’appels (IACL Comms) n’est pas configuré. Les messages fonctionnent normalement.',
        not_recording: 'Cet appel n’est pas enregistré.',
        recording_limit: 'La durée maximale d’enregistrement est atteinte.',
      },
      related: {
        tab: 'Ce qui s’est dit ici',
        open: 'En parler ici',
        openHint: 'Ouvre la conversation de cet enregistrement dans Radio',
      },
      dashboard: {
        pending: 'Propositions de l’assistant sans réponse',
        pendingHelp: 'Ce que l’assistant a tiré de vos conversations et qui attend que quelqu’un le note ou l’écarte.',
        unread: 'Messages non lus dans Radio',
        unreadHelp: 'Messages de vos conversations que vous n’avez pas encore vus.',
      },
      notifications: {
        knock: { title: '{name} veut entrer', message: '{title}' },
        categories: {
          meetingKnock: 'Radio : quelqu’un veut entrer dans une réunion',
          message: 'Radio : nouveaux messages',
          call: 'Radio : appels entrants',
          missedCall: 'Radio : appels manqués',
          proposal: 'Radio : propositions de l’assistant',
          budget: 'Radio : budget IA épuisé',
        },
        ring: {
          title: '{name} vous appelle',
          titleIn: '{name} vous appelle · {where}',
          voice: 'Appel vocal',
          video: 'Appel vidéo',
          answer: 'Répondre',
          decline: 'Refuser',
        },
        message: {
          voice: 'Note vocale',
          photo: 'Photo',
          photoWithText: 'Photo : {text}',
          video: 'Vidéo',
          videoWithText: 'Vidéo : {text}',
          location: 'Position',
          file: 'Fichier : {name}',
        },
        missed: { title: 'Appel manqué de {name}', message: 'Vous a appelé par Radio.' },
        proposal: { title: 'L’assistant a quelque chose à noter', message: '{where}' },
        budget: {
          title: 'L’assistant de Radio a dépensé le budget du mois',
          message: 'Les messages et les appels continuent. Rien n’est transcrit ni proposé jusqu’au mois prochain ou jusqu’à ce que le plafond soit relevé (AC.10, {limit} €).',
        },
      },
    },
    assistant: {
      openers: {
        comms: { 'what-said': 'Qu’est-ce qui s’est dit aujourd’hui sur le chantier ?' },
      },
    },
    modules: { comms: 'Radio' },
    permissions: {
      comms: {
        use: 'Parler par Radio : messages, notes vocales et conversations des enregistrements',
        call: 'Passer des appels et des appels vidéo par Radio',
        meetings: 'Organiser des réunions : les créer, les modifier et les annuler (pas besoin pour y participer)',
        groups: 'Créer des groupes et gérer qui en fait partie',
        external: 'Ajouter à une conversation quelqu’un d’extérieur (portail clients)',
        oversee: 'Lire les conversations de groupe et d’enregistrements sans en faire partie (c’est enregistré ; jamais les privées)',
      },
    },
    settings: {
      ns: {
        comms: {
          title: 'Radio',
          subtitle: 'Le canal de l’entreprise, ses appels et son assistant',
          groups: { copilot: 'Assistant', recording: 'Enregistrement des appels', calls: 'Appels', retention: 'Conservation', limits: 'Limites' },
          copilot: { label: 'Assistant activé', help: 'Désactivé, le canal fonctionne entièrement —messages, notes et appels— mais rien n’est transcrit, décrit ni proposé.' },
          extractAfterMessages: { label: 'Messages qui déclenchent la lecture', help: 'L’assistant lit une conversation quand elle devient silencieuse, ou avant si ce nombre de messages s’accumule.' },
          extractAfterSilenceSeconds: { label: 'Silence avant lecture', help: 'Secondes sans message avant que l’assistant lise ce qui a été dit.' },
          recordCalls: { label: 'Enregistrer les appels', help: 'Chaque participant enregistre son propre micro. Tout le monde voit pendant tout l’appel qu’il est enregistré, et la conversation l’indique. Informez d’abord le personnel par écrit (arts. 88-90 LOPDGDD).' },
          recordingRetentionDays: { label: 'Jours de conservation des enregistrements', help: 'Ensuite l’audio est effacé. Ce qui a été dit reste en texte tant que les messages sont conservés.' },
          messageRetentionMonths: { label: 'Mois de conservation des messages', help: 'Les messages, photos, positions et transcriptions plus anciens sont effacés chaque nuit.' },
          ringSeconds: { label: 'Durée de sonnerie', help: 'Secondes de sonnerie avant de considérer l’appel comme manqué et de proposer de laisser une note vocale.' },
          maxVideoParticipants: { label: 'Personnes dans un appel vidéo', help: 'Au-delà, l’appel vidéo est proposé en audio. Cinq ou six seulement avec un bon réseau : chaque appareil envoie une vidéo par personne et la qualité baisse pour tenir.' },
          maxAudioParticipants: { label: 'Personnes dans un appel audio', help: 'Le nombre maximal de personnes dans un même appel audio.' },
          monthlyAiBudgetEur: { label: 'Budget IA mensuel', help: 'Quand l’assistant dépense ce montant dans le mois, il s’arrête jusqu’au mois suivant et les administrateurs sont prévenus. Le canal continue de fonctionner. Vide signifie sans plafond propre ; 0 signifie qu’il ne dépense rien.' },
          describePhotos: { label: 'Décrire les photos', help: 'L’assistant regarde chaque photo pour qu’une photo seule puisse aboutir à une proposition.' },
          groupMaxMembers: { label: 'Personnes dans un groupe', help: 'Au-delà de cent, c’est une liste de diffusion, pas une conversation.' },
          reviewAfterHours: { label: 'Heures avant de passer en revue', help: 'Une proposition restée sans réponse aussi longtemps apparaît aussi dans « À revoir ».' },
          proposalExpiryDays: { label: 'Durée de vie d’une proposition (jours)', help: 'Passé ce délai, une proposition sans réponse expire : ce qui a été dit n’est sans doute plus d’actualité.' },
          voiceNoteMaxSeconds: { label: 'Note vocale la plus longue', help: 'Personne n’écoute plus de cinq minutes.' },
          recordingMaxMinutes: { label: 'Enregistrement le plus long', help: 'L’enregistrement s’arrête ici et l’appel l’indique. L’appel continue.' },
        },
      },
    },
    audit: {
      actions: {
        comms: {
          meeting_created: 'Réunion créée',
          meeting_link_regenerated: 'Lien de réunion régénéré',
          meeting_link_revoked: 'Lien de réunion désactivé',
          meeting_cancelled: 'Réunion annulée',
          meeting_ended: 'Réunion terminée',
          meeting_started: 'Réunion commencée',
          meeting_recording_enabled: 'Enregistrement de réunion activé',
          meeting_guest_requested: 'Un invité externe a demandé à entrer dans une réunion',
          meeting_guest_admitted: 'Invité externe admis dans une réunion',
          meeting_guest_device_added: 'Invité externe a ajouté un autre appareil à une réunion',
          meeting_guest_rejected: 'Invité externe refusé dans une réunion',
          meeting_guest_removed: 'Invité externe expulsé d’une réunion',
          conversation_overseen: 'Conversation lue en supervision',
          copilot_on: 'Assistant activé dans une conversation',
          copilot_off: 'Assistant désactivé dans une conversation',
          proposal_accepted: 'Proposition de l’assistant acceptée',
          proposal_edited: 'Proposition de l’assistant corrigée avant confirmation',
          proposal_rejected: 'Proposition de l’assistant écartée',
          proposal_failed: 'Proposition de l’assistant qui n’a pas pu être faite',
          group_created: 'Groupe créé',
          group_renamed: 'Groupe renommé',
          members_added: 'Personnes ajoutées à un groupe',
          member_removed: 'Personne retirée d’un groupe',
          conversation_archived: 'Conversation archivée',
          conversation_unarchived: 'Conversation sortie des archives',
        },
      },
    },
  },
}


// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'
// The reasons and field codes its errors carry (frontend/i18n/errorReasons.js).
import { withErrorReasons } from './i18n/errorReasons.js'

export const messages = withErrorReasons(withToolLabels(withAnalytics(baseMessages, 'comms')))

// Las tarjetas del asistente (comms.assistant.*), en su propio fichero: son un
// contrato con backend/assistant.js.
for (const [locale, block] of Object.entries(assistantMessages)) {
  messages[locale].comms.assistant = { ...messages[locale].comms.assistant, ...block }
}
